import { useApi } from '../../hooks/useApi';
import { uploadBlogImage, getS3Url } from '../../utils/s3Image';
import {
  Activity,
  BarChart3,
  Brain,
  Building,
  Tag,
  Clock,
  DollarSign,
  Edit,
  Eye,
  Globe,
  Search,
  Settings,
  Trash2,
  UserPlus,
  Users,
  ArrowRightLeft,
  FileText,
  Plus,
  EyeOff,
  CheckCircle,
  XCircle,
  Mail,
  Download,
  Ban,
  ShieldOff,
  UserCheck
} from 'lucide-react';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { getAdminDesignSystem, ADMIN_ROOT, AdminThemeProvider } from './adminDesignSystem'; // Shared admin design system (visual-only)
import { KpiStrip, AdminToolbar, OrgCell, StatusBadge, statusTone, ColHeader } from './AdminUI'; // Soft AntD building blocks
import AdminSidebar from './AdminSidebar'; // Shared grouped/collapsible sidebar (one nav for all admin pages)
import McpPlatformKeyCard from './McpPlatformKeyCard'; // ADDITIVE: main_admin platform MCP key (Claude Code)
import PartnersManager from './PartnersManager'; // B2B Partner Portal (isolated)
import ActivitySummary from './ActivitySummary'; // User Activity Summary (isolated)
import PostHogAnalytics from './PostHogAnalytics'; // PostHog analytics (isolated)
import EmailCampaigns from './EmailCampaigns'; // Email campaigns (isolated)
import ReportGenerator from './ReportGenerator'; // Sales-demo report generator (isolated, client-side)
import { Table, Pagination, Skeleton, Checkbox, Button as AntButton, Modal } from 'antd';
import TipTapEditor from '../../components/TiptapEditor/TipTapEditor';
import * as XLSX from 'xlsx';


// Add custom styles for table (works for both dark and light mode)
const getTableStyles = (isDark) => `
  .custom-table .ant-table {
    background: ${isDark ? 'transparent' : '#ffffff'} !important;
  }
  .custom-table .ant-table-container {
    background: ${isDark ? 'transparent' : '#ffffff'} !important;
  }
  .custom-table .ant-table-body {
    background: ${isDark ? 'transparent' : '#ffffff'} !important;
  }
  .custom-table .ant-table-thead > tr > th {
    background: ${isDark ? 'rgba(255, 255, 255, 0.05)' : '#fafafa'} !important;
    color: ${isDark ? '#ffffff' : '#1f2937'} !important;
    border-bottom: ${isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)'} !important;
    font-weight: ${isDark ? '600' : '700'} !important;
    font-size: ${isDark ? 'inherit' : '13px'} !important;
    position: relative !important;
  }
  .custom-table .ant-table-thead > tr > th:not(:last-child)::after {
    content: '' !important;
    position: absolute !important;
    right: 0 !important;
    top: 25% !important;
    bottom: 25% !important;
    width: 1px !important;
    background: ${isDark ? 'rgba(148, 163, 184, 0.4)' : 'rgba(0, 0, 0, 0.2)'} !important;
  }
  .custom-table .ant-table-tbody > tr > td {
    background: ${isDark ? 'transparent' : '#ffffff'} !important;
    color: ${isDark ? '#ffffff' : '#1f2937'} !important;
    border-bottom: ${isDark ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(0, 0, 0, 0.06)'} !important;
    padding: 16px 20px !important;
    transition: all 0.2s ease !important;
  }
  .custom-table .ant-table-tbody > tr:hover > td {
    background: ${isDark ? 'rgba(255, 255, 255, 0.08)' : '#f9fafb'} !important;
  }
  .custom-table .ant-table-tbody > tr.ant-table-row-selected > td {
    background: ${isDark ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.08)'} !important;
  }
  .custom-table .ant-checkbox-wrapper {
    color: ${isDark ? '#ffffff' : '#000000'} !important;
  }
  .custom-table .ant-checkbox-inner {
    background-color: ${isDark ? 'rgba(0, 0, 0, 0.5)' : '#ffffff'} !important;
    border-color: ${isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.25)'} !important;
  }
  .custom-table .ant-checkbox-checked .ant-checkbox-inner {
    background-color: #3b82f6 !important;
    border-color: #3b82f6 !important;
  }
  .custom-table .ant-pagination {
    color: ${isDark ? '#ffffff' : '#000000'} !important;
  }
  .custom-table .ant-pagination-item {
    background: ${isDark ? 'rgba(0, 0, 0, 0.3)' : '#ffffff'} !important;
    border-color: ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.15)'} !important;
  }
  .custom-table .ant-pagination-item a {
    color: ${isDark ? '#ffffff' : '#000000'} !important;
  }
  .custom-table .ant-pagination-item-active {
    background: ${isDark ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)'} !important;
    border-color: #3b82f6 !important;
  }
  .custom-table .ant-pagination-item-active a {
    color: ${isDark ? '#ffffff' : '#3b82f6'} !important;
  }
  .custom-table .ant-pagination-prev,
  .custom-table .ant-pagination-next {
    color: ${isDark ? '#ffffff' : '#000000'} !important;
  }
  .custom-table .ant-pagination-prev:hover,
  .custom-table .ant-pagination-next:hover {
    color: #60a5fa !important;
  }
  .custom-table .ant-pagination-disabled {
    opacity: 0.3 !important;
  }
  .custom-table .ant-select-selector {
    background: ${isDark ? 'rgba(0, 0, 0, 0.3)' : '#ffffff'} !important;
    border-color: ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.15)'} !important;
    color: ${isDark ? '#ffffff' : '#000000'} !important;
  }
  .custom-table .ant-select-selection-item {
    color: ${isDark ? '#ffffff' : '#000000'} !important;
  }
  .custom-table .ant-pagination-options {
    color: ${isDark ? '#ffffff' : '#000000'} !important;
  }
  .custom-table .ant-pagination-options .ant-select {
    color: ${isDark ? '#ffffff' : '#000000'} !important;
  }
  .custom-table .ant-pagination-total-text {
    color: ${isDark ? '#ffffff' : '#000000'} !important;
  }
  .custom-table .ant-pagination-item-link {
    color: ${isDark ? '#ffffff' : '#000000'} !important;
  }
  .custom-table .ant-pagination-item-link:hover {
    color: #60a5fa !important;
  }
  .custom-table .ant-pagination-jump-prev .ant-pagination-item-container .ant-pagination-item-link-icon,
  .custom-table .ant-pagination-jump-next .ant-pagination-item-container .ant-pagination-item-link-icon {
    color: ${isDark ? '#ffffff' : '#000000'} !important;
  }
  button.bg-blue-600,
  button.bg-blue-600 * {
    color: #ffffff !important;
  }
  .dark-modal .ant-modal-content {
    background: rgba(0, 0, 0, 0.9) !important;
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
  }
  .dark-modal .ant-modal-header {
    background: rgba(0, 0, 0, 0.9) !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
  }
  .dark-modal .ant-modal-body {
    background: rgba(0, 0, 0, 0.9) !important;
    color: #ffffff !important;
  }
  .dark-modal .ant-modal-footer {
    background: rgba(0, 0, 0, 0.9) !important;
    border-top: 1px solid rgba(255, 255, 255, 0.1) !important;
  }
  .dark-modal .ant-modal-close {
    color: #ffffff !important;
  }
  .dark-modal .ant-modal-close:hover {
    color: #ef4444 !important;
  }
`;


const MainAdmin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const api = useApi();
  const { mode } = useTheme();
  const isDark = mode === 'dark';
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
    industry: '',
    plan: 'startup',
    subscriptionStatus: 'active',
    billingEnabled: true,
  });

  // Scheduler Logs State
  const [schedulerLogs, setSchedulerLogs] = useState([]);
  const [schedulerStats, setSchedulerStats] = useState({});
  const [schedulerLoading, setSchedulerLoading] = useState(false);
  // Admin Stats State
  const [adminStats, setAdminStats] = useState({
    activeUsersToday: 0
  });
  const [activeTab, setActiveTab] = useState('organizations'); // 'organizations', 'scheduler', 'ai-prompts', 'system-logics', or 'blogs'
  const [schedulerPage] = useState(1);
  const [schedulerLimit] = useState(10);

  // AI Prompts State
  const [aiPrompts, setAiPrompts] = useState([]);
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [promptFormData, setPromptFormData] = useState({
    promptType: 'hourly',
    title: '',
    prompt: '',
    description: '',
    isActive: true,
    gptModel: 'gpt-3.5-turbo',
    maxTokens: 1000,
    temperature: 0.7
  });

  // Blog Management State
  const [blogs, setBlogs] = useState([]);
  const [blogsLoading, setBlogsLoading] = useState(false);
  const [showBlogModal, setShowBlogModal] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [blogFormData, setBlogFormData] = useState({
    title: '',
    slug: '',
    description: '',
    content: '',
    category: 'Productivity',
    product: 'Teamgrid',
    author: '',
    authorInitials: '',
    readTime: '5 min',
    featuredImage: '',
    images: [], // Array of S3 keys
    isLive: false,
    faqs: [],
    metaTitle: '',
    metaDescription: '',
    keywords: [],
    canonicalUrl: ''
  });
  const [showSeoSettings, setShowSeoSettings] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]); // Store uploaded image previews
  const [uploadingFeaturedImage, setUploadingFeaturedImage] = useState(false);
  const [featuredImagePreview, setFeaturedImagePreview] = useState(null);
  
  // Blog Pagination State
  const [blogCurrentPage, setBlogCurrentPage] = useState(1);
  const [blogPageSize, setBlogPageSize] = useState(10);
  const [blogTotal, setBlogTotal] = useState(0);
  const quillRef = useRef(null); // Ref for ReactQuill editor
  const tipTapEditorRef = useRef(null); // Ref for TipTap editor

  // Newsletter Subscribers State
  const [newsletterSubscribers, setNewsletterSubscribers] = useState([]);
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterSearch, setNewsletterSearch] = useState('');
  const [newsletterPage, setNewsletterPage] = useState(1);
  const [newsletterPageSize, setNewsletterPageSize] = useState(5);
  const [newsletterTotal, setNewsletterTotal] = useState(0);

  // Leads (Demo Requests) State
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsSearch, setLeadsSearch] = useState('');
  const [leadsStatusFilter, setLeadsStatusFilter] = useState('');
  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsPageSize, setLeadsPageSize] = useState(25);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [blockedIps, setBlockedIps] = useState([]);
  const [blockedIpsLoading, setBlockedIpsLoading] = useState(false);
  const [leadActionLoadingId, setLeadActionLoadingId] = useState(null);

  const plans = [
    { value: 'startup', label: 'Startup', color: 'bg-green-500/20 text-green-400' },
    { value: 'growth', label: 'Growth', color: 'bg-blue-500/20 text-blue-400' },
    { value: 'enterprise', label: 'Enterprise', color: 'bg-purple-500/20 text-purple-400' },
  ];

  const statuses = [
    { value: 'active', label: 'Active', color: 'bg-green-500/20 text-green-400' },
    { value: 'past_due', label: 'Past Due', color: 'bg-yellow-500/20 text-yellow-400' },
    { value: 'canceled', label: 'Canceled', color: 'bg-red-500/20 text-red-400' },
  ];

  const fetchOrganizations = useCallback(async () => {
    try {
      const response = await api.get('/api/v1/admin/organizations');
      setOrganizations(response.data.data.organizations);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSchedulerLogs = useCallback(async () => {
    try {
      setSchedulerLoading(true);
      const response = await api.get('/api/v1/scheduler-logs', {
        params: { page: schedulerPage, limit: schedulerLimit }
      });
      setSchedulerLogs(response.data.data);
    } catch (error) {
      console.error('Error fetching scheduler logs:', error);
    } finally {
      setSchedulerLoading(false);
    }
  }, [schedulerPage, schedulerLimit]);

  const fetchSchedulerStats = useCallback(async () => {
    try {
      const response = await api.get('/api/v1/scheduler-logs/stats');
      setSchedulerStats(response.data.data);
    } catch (error) {
      console.error('Error fetching scheduler stats:', error);
    }
  }, []);

  const fetchAdminStats = useCallback(async () => {
    try {
      const response = await api.get('/api/v1/admin/stats');
      if (response.data?.data?.stats?.activeUsersToday !== undefined) {
        setAdminStats({ activeUsersToday: response.data.data.stats.activeUsersToday });
      }
    } catch (error) {
      console.error('Error fetching admin stats:', error);
    }
  }, [api]);

  // AI Prompts Functions
  const fetchAIPrompts = useCallback(async () => {
    try {
      setPromptsLoading(true);
      const response = await api.get('/api/v1/admin/ai-prompts');
      setAiPrompts(response.data.data);
    } catch (error) {
      console.error('Error fetching AI prompts:', error);
    } finally {
      setPromptsLoading(false);
    }
  }, []);

  const handleAddPrompt = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/v1/admin/ai-prompts', promptFormData);
      setShowPromptModal(false);
      resetPromptForm();
      fetchAIPrompts();
    } catch (error) {
      console.error('Error adding AI prompt:', error);
    }
  };

  const handleEditPrompt = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/api/v1/admin/ai-prompts/${selectedPrompt._id}`, promptFormData);
      setShowPromptModal(false);
      setSelectedPrompt(null);
      resetPromptForm();
      fetchAIPrompts();
    } catch (error) {
      console.error('Error updating AI prompt:', error);
    }
  };

  const handleDeletePrompt = async (promptId) => {
    if (window.confirm('Are you sure you want to delete this AI prompt?')) {
      try {
        await api.delete(`/api/v1/admin/ai-prompts/${promptId}`);
        fetchAIPrompts();
      } catch (error) {
        console.error('Error deleting AI prompt:', error);
      }
    }
  };

  const openPromptEditModal = (prompt) => {
    setSelectedPrompt(prompt);
    setPromptFormData({
      promptType: prompt.promptType,
      title: prompt.title,
      prompt: prompt.prompt,
      description: prompt.description || '',
      isActive: prompt.isActive,
      gptModel: prompt.gptModel || 'gpt-3.5-turbo',
      maxTokens: prompt.maxTokens || 1000,
      temperature: prompt.temperature || 0.7
    });
    setShowPromptModal(true);
  };

  const resetPromptForm = () => {
    setPromptFormData({
      promptType: 'hourly',
      title: '',
      prompt: '',
      description: '',
      isActive: true,
      gptModel: 'gpt-3.5-turbo',
      maxTokens: 1000,
      temperature: 0.7
    });
  };

  // Blog Management Functions
  const fetchBlogs = useCallback(async () => {
    try {
      setBlogsLoading(true);
      const response = await api.get('/api/v1/blogs/admin/all', {
        params: {
          page: blogCurrentPage,
          limit: blogPageSize
        }
      });
      setBlogs(response.data.data.blogs);
      setBlogTotal(response.data.data.pagination.total);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      toast.error('Failed to fetch blogs');
    } finally {
      setBlogsLoading(false);
    }
  }, [api, blogCurrentPage, blogPageSize]);

  useEffect(() => {
    if (activeTab === 'blogs') {
      fetchBlogs();
    }
  }, [activeTab, fetchBlogs]);

  // Honor ?tab=<key> so the shared sidebar can deep-link back from sibling
  // pages (e.g. Coupons → "Scheduler Logs") to the correct in-page tab.
  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (tab) {
      setActiveTab(tab);
      if (tab === 'blogs') resetBlogPagination();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Blog Pagination Handlers
  const handleBlogPageChange = (page, size) => {
    setBlogCurrentPage(page);
    if (size && size !== blogPageSize) {
      setBlogPageSize(size);
    }
  };

  const resetBlogPagination = () => {
    setBlogCurrentPage(1);
    setBlogPageSize(10);
  };

  // Newsletter Subscribers Fetch
  const fetchNewsletterSubscribers = useCallback(async () => {
    try {
      setNewsletterLoading(true);
      const response = await api.get('/api/v1/newsletter/subscribers', {
        params: { page: newsletterPage, limit: newsletterPageSize, search: newsletterSearch }
      });
      setNewsletterSubscribers(response.data.data.subscribers);
      setNewsletterTotal(response.data.data.pagination.total);
    } catch (error) {
      console.error('Error fetching newsletter subscribers:', error);
      toast.error('Failed to fetch newsletter subscribers');
    } finally {
      setNewsletterLoading(false);
    }
  }, [api, newsletterPage, newsletterPageSize, newsletterSearch]);

  useEffect(() => {
    if (activeTab === 'newsletter') {
      fetchNewsletterSubscribers();
    }
  }, [activeTab, fetchNewsletterSubscribers]);

  // Leads (Demo Requests) Fetchers
  const fetchLeads = useCallback(async () => {
    try {
      setLeadsLoading(true);
      const params = { page: leadsPage, limit: leadsPageSize };
      if (leadsSearch.trim()) params.search = leadsSearch.trim();
      if (leadsStatusFilter) params.status = leadsStatusFilter;
      const response = await api.get('/api/v1/admin/demo-requests', { params });
      setLeads(response.data.data.items || []);
      setLeadsTotal(response.data.data.pagination?.total || 0);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to fetch leads');
    } finally {
      setLeadsLoading(false);
    }
  }, [api, leadsPage, leadsPageSize, leadsSearch, leadsStatusFilter]);

  const fetchBlockedIps = useCallback(async () => {
    try {
      setBlockedIpsLoading(true);
      const response = await api.get('/api/v1/admin/blocked-ips');
      setBlockedIps(response.data.data.items || []);
    } catch (error) {
      console.error('Error fetching blocked IPs:', error);
    } finally {
      setBlockedIpsLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (activeTab === 'leads') {
      fetchLeads();
      fetchBlockedIps();
    }
  }, [activeTab, fetchLeads, fetchBlockedIps]);

  const handleLeadStatusChange = async (leadId, status) => {
    try {
      setLeadActionLoadingId(leadId);
      await api.patch(`/api/v1/admin/demo-requests/${leadId}/status`, { status });
      toast.success(`Status updated to ${status}`);
      fetchLeads();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update status');
    } finally {
      setLeadActionLoadingId(null);
    }
  };

  const handleBlockLeadIp = (lead) => {
    Modal.confirm({
      title: `Block IP ${lead.ipAddress}?`,
      content: `This will reject all future submissions from this IP. Lead "${lead.email}" will be marked as blocked.`,
      okText: 'Block IP',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setLeadActionLoadingId(lead._id);
          await api.post(`/api/v1/admin/demo-requests/${lead._id}/block-ip`);
          toast.success(`IP ${lead.ipAddress} blocked`);
          fetchLeads();
          fetchBlockedIps();
        } catch (error) {
          toast.error(error?.response?.data?.message || 'Failed to block IP');
        } finally {
          setLeadActionLoadingId(null);
        }
      },
    });
  };

  const handleDeleteLead = (lead) => {
    Modal.confirm({
      title: 'Delete this lead?',
      content: `${lead.fullName} <${lead.email}> will be permanently removed.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setLeadActionLoadingId(lead._id);
          await api.delete(`/api/v1/admin/demo-requests/${lead._id}`);
          toast.success('Lead deleted');
          fetchLeads();
        } catch (error) {
          toast.error(error?.response?.data?.message || 'Failed to delete lead');
        } finally {
          setLeadActionLoadingId(null);
        }
      },
    });
  };

  const handleUnblockIp = (ip) => {
    Modal.confirm({
      title: `Unblock IP ${ip}?`,
      content: 'This IP will be allowed to submit forms again.',
      okText: 'Unblock',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await api.delete(`/api/v1/admin/blocked-ips/${encodeURIComponent(ip)}`);
          toast.success(`IP ${ip} unblocked`);
          fetchBlockedIps();
          fetchLeads();
        } catch (error) {
          toast.error(error?.response?.data?.message || 'Failed to unblock IP');
        }
      },
    });
  };

  const handleExportSubscribers = async () => {
    try {
      toast.loading('Exporting subscribers...', { id: 'export' });
      const response = await api.get('/api/v1/newsletter/subscribers', {
        params: { page: 1, limit: 99999, search: '' }
      });
      const allSubs = response.data.data.subscribers;
      if (!allSubs.length) {
        toast.error('No subscribers to export', { id: 'export' });
        return;
      }
      const data = allSubs.map((sub, i) => ({
        '#': i + 1,
        'Email': sub.email,
        'Subscribed Date': new Date(sub.subscribedAt).toLocaleDateString('en-US', {
          year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
        })
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Subscribers');
      XLSX.writeFile(wb, `newsletter_subscribers_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success(`Exported ${allSubs.length} subscribers`, { id: 'export' });
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export subscribers', { id: 'export' });
    }
  };

  const handleAddBlog = async (e) => {
    e.preventDefault();
    try {
      console.log('Submitting blog with featuredImage:', blogFormData.featuredImage); // Debug log
      await api.post('/api/v1/blogs/admin', blogFormData);
      toast.success('Blog created successfully');
      setShowBlogModal(false);
      resetBlogForm();
      resetBlogPagination(); // Reset to first page to show new blog
      fetchBlogs();
    } catch (error) {
      console.error('Error creating blog:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to create blog');
    }
  };

  const handleEditBlog = async (e) => {
    e.preventDefault();
    try {
      console.log('Updating blog with featuredImage:', blogFormData.featuredImage); // Debug log
      await api.put(`/api/v1/blogs/admin/${selectedBlog._id}`, blogFormData);
      toast.success('Blog updated successfully');
      setShowBlogModal(false);
      setSelectedBlog(null);
      resetBlogForm();
      fetchBlogs();
    } catch (error) {
      console.error('Error updating blog:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to update blog');
    }
  };

  const handleDeleteBlog = async (blogId) => {
    if (window.confirm('Are you sure you want to delete this blog?')) {
      try {
        await api.delete(`/api/v1/blogs/admin/${blogId}`);
        toast.success('Blog deleted successfully');
        
        // If we're on the last page and it only has 1 item, go to previous page
        if (blogs.length === 1 && blogCurrentPage > 1) {
          setBlogCurrentPage(blogCurrentPage - 1);
        } else {
          fetchBlogs();
        }
      } catch (error) {
        console.error('Error deleting blog:', error);
        toast.error('Failed to delete blog');
      }
    }
  };

  const handleToggleLive = async (blogId, currentStatus) => {
    try {
      await api.patch(`/api/v1/blogs/admin/${blogId}/toggle-live`);
      toast.success(`Blog ${!currentStatus ? 'published' : 'unpublished'} successfully`);
      fetchBlogs();
    } catch (error) {
      console.error('Error toggling blog status:', error);
      toast.error('Failed to update blog status');
    }
  };

  const openBlogEditModal = async (blog) => {
    setSelectedBlog(blog);
    setBlogFormData({
      title: blog.title,
      slug: blog.slug || '',
      description: blog.description,
      content: blog.content,
      category: blog.category,
      product: blog.product || 'Teamgrid',
      author: blog.author,
      authorInitials: blog.authorInitials,
      readTime: blog.readTime,
      featuredImage: blog.featuredImage || '',
      images: blog.images || [],
      isLive: blog.isLive,
      faqs: blog.faqs || [],
      metaTitle: blog.metaTitle || '',
      metaDescription: blog.metaDescription || '',
      keywords: blog.keywords || [],
      canonicalUrl: blog.canonicalUrl || ''
    });
    setShowSeoSettings(!!(blog.metaTitle || blog.metaDescription || (blog.keywords && blog.keywords.length > 0) || blog.canonicalUrl));
    // Set the featured image preview for editing
    setFeaturedImagePreview(blog.featuredImage || null);
    console.log('Loading blog for edit with featuredImage:', blog.featuredImage); // Debug log
    
    // Load image previews for existing images
    if (blog.images && blog.images.length > 0) {
      const previews = await Promise.all(
        blog.images.map(async (key) => {
          const url = await getS3Url(key);
          return { key, url };
        })
      );
      setUploadedImages(previews.filter(p => p.url !== null));
    } else {
      setUploadedImages([]);
    }
    
    setShowBlogModal(true);
  };

  const resetBlogForm = () => {
    setBlogFormData({
      title: '',
      slug: '',
      description: '',
      content: '',
      category: 'Productivity',
      product: 'Teamgrid',
      author: '',
      authorInitials: '',
      readTime: '5 min',
      featuredImage: '',
      images: [],
      isLive: false,
      faqs: [],
      metaTitle: '',
      metaDescription: '',
      keywords: [],
      canonicalUrl: ''
    });
    setShowSeoSettings(false);
    setUploadedImages([]);
    setFeaturedImagePreview(null);
    setSelectedBlog(null);
  };

  const handleImageUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploadingImages(true);
    const uploadedKeys = [];
    const newPreviews = [];

    try {
      for (const file of Array.from(files)) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} is not an image file`);
          continue;
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} is too large. Maximum size is 10MB`);
          continue;
        }

        const blogId = selectedBlog?._id || null;
        const result = await uploadBlogImage(file, blogId, api);

        if (result.success) {
          uploadedKeys.push(result.key);
          // Generate preview URL
          const previewUrl = await getS3Url(result.key);
          if (previewUrl) {
            newPreviews.push({ key: result.key, url: previewUrl });
          }
        } else {
          toast.error(`Failed to upload ${file.name}: ${result.error}`);
        }
      }

      // Update form data with new image keys
      setBlogFormData({
        ...blogFormData,
        images: [...blogFormData.images, ...uploadedKeys]
      });
      setUploadedImages([...uploadedImages, ...newPreviews]);
      toast.success(`${uploadedKeys.length} image(s) uploaded successfully`);
    } catch (error) {
      console.error('Error uploading images:', error);
      toast.error('Failed to upload images');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleRemoveImage = (keyToRemove) => {
    setBlogFormData({
      ...blogFormData,
      images: blogFormData.images.filter(key => key !== keyToRemove)
    });
    setUploadedImages(uploadedImages.filter(img => img.key !== keyToRemove));
  };

  const handleFeaturedImageUpload = async (file) => {
    if (!file) return;

    // Show immediate preview using FileReader
    const reader = new FileReader();
    reader.onload = (e) => {
      setFeaturedImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);

    setUploadingFeaturedImage(true);
    try {
      const result = await uploadBlogImage(file, null, api);
      if (!result.success) {
        throw new Error(result.error);
      }
      const imageUrl = await getS3Url(result.key);
      
      setBlogFormData({
        ...blogFormData,
        featuredImage: imageUrl
      });
      setFeaturedImagePreview(imageUrl); // Update with S3 URL after upload
      toast.success('Featured image uploaded successfully');
    } catch (error) {
      console.error('Error uploading featured image:', error);
      toast.error('Failed to upload featured image');
      setFeaturedImagePreview(null); // Clear preview on error
    } finally {
      setUploadingFeaturedImage(false);
    }
  };

  const removeFeaturedImage = () => {
    setBlogFormData({
      ...blogFormData,
      featuredImage: ''
    });
    setFeaturedImagePreview(null);
  };

  // Custom image handler for TipTap editor
  const handleImageUploadInEditor = useCallback(async () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`);
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Maximum size is 10MB`);
        return;
      }

      try {
        setUploadingImages(true);
        const blogId = selectedBlog?._id || null;
        const result = await uploadBlogImage(file, blogId, api);

        if (result.success) {
          // Get S3 URL
          const imageUrl = await getS3Url(result.key);
          
          if (imageUrl && tipTapEditorRef.current) {
            const editor = tipTapEditorRef.current;
            
            // Insert image at cursor position
            editor.chain().focus().setImage({ src: imageUrl }).run();
            
            toast.success('Image inserted successfully');
          } else {
            toast.error('Failed to get image URL');
          }
        } else {
          toast.error(`Failed to upload image: ${result.error}`);
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        toast.error('Failed to upload image');
      } finally {
        setUploadingImages(false);
      }
    };
  }, [selectedBlog, api]);


  // Get max tokens for selected model
  const getMaxTokensForModel = (model) => {
    const limits = {
      'gpt-3.5-turbo': 4096,
      'gpt-4': 8192,
      'gpt-4-turbo': 4096,
      'gpt-4o': 4096,
      'gpt-4o-mini': 16384
    };
    return limits[model] || 4096;
  };

  // Handle model change and update max tokens if needed
  const handleModelChange = (newModel) => {
    const modelMaxTokens = getMaxTokensForModel(newModel);
    const currentTokens = promptFormData.maxTokens;
    
    setPromptFormData({
      ...promptFormData,
      gptModel: newModel,
      maxTokens: currentTokens > modelMaxTokens ? modelMaxTokens : currentTokens
    });
  };

  // Redirect non-main-admin users to dashboard
  useEffect(() => {
    if (user && user.role !== 'main_admin') {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchOrganizations();
    fetchSchedulerStats();
    fetchAdminStats();
  }, [fetchOrganizations, fetchSchedulerStats, fetchAdminStats]);

  useEffect(() => {
    if (activeTab === 'scheduler') {
      fetchSchedulerLogs();
    } else if (activeTab === 'ai-prompts') {
      fetchAIPrompts();
    }
  }, [activeTab, fetchSchedulerLogs, fetchAIPrompts]);


  const [switchingOrgId, setSwitchingOrgId] = useState(null);

  const switchToOrganization = async (organizationId) => {
    setSwitchingOrgId(organizationId); // Start animation
    try {
      const response = await api.post(`/api/v1/admin/login-as-organization/${organizationId}`);
      
      if (response.data.success) {
        // Store new tokens
        localStorage.setItem('token', response.data.data.token);
        localStorage.setItem('refreshToken', response.data.data.refreshToken);
        
        // Update user in AuthContext with isMainAdminSwitch flag
        const updatedUser = {
          ...response.data.data.user,
          isMainAdminSwitch: true,
          originalMainAdminId: response.data.data.user.originalMainAdminId
        };
        
        // Store user data in localStorage for AuthContext to pick up
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        // Set localStorage flag to track active switch session
        localStorage.setItem('isMainAdminSwitch', 'true');
        localStorage.setItem('mainAdminSwitchUserId', response.data.data.user.id);
        
        // Show success message
        toast.success(`Switched to ${response.data.data.organization.name} successfully!`);
        
        // Redirect to organization dashboard
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 500);
      }
    } catch (error) {
      console.error('Error switching organization:', error);
      toast.error(error.response?.data?.message || 'Failed to switch organization');
      setSwitchingOrgId(null); // Stop animation on error
    }
  };

  const handleAddOrganization = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/v1/admin/organizations', formData);
      toast.success('Organization created successfully!');
      setShowAddModal(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        website: '',
        industry: '',
        plan: 'startup',
        subscriptionStatus: 'active',
        billingEnabled: true,
      });
      fetchOrganizations();
    } catch (error) {
      console.error('Error adding organization:', error);
      const errorMessage = error.response?.data?.error?.message || error.response?.data?.message || error.message || 'Failed to create organization';
      toast.error(errorMessage);
    }
  };

  const handleEditOrganization = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/api/v1/admin/organizations/${selectedOrganization._id}`, formData);
      toast.success('Organization updated successfully!');
      setShowEditModal(false);
      setSelectedOrganization(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        website: '',
        industry: '',
        plan: 'startup',
        subscriptionStatus: 'active',
        billingEnabled: true,
        billingType: 'recurring',
        autoPayEnabled: true,
      });
      fetchOrganizations();
    } catch (error) {
      console.error('Error updating organization:', error);
      const errorMessage = error.response?.data?.error?.message || error.response?.data?.message || error.message || 'Failed to update organization';
      toast.error(errorMessage);
    }
  };

  const handleDeleteOrganization = async (organizationId) => {
    if (window.confirm('Are you sure you want to archive this organization?')) {
      try {
        await api.delete(`/api/v1/admin/organizations/${organizationId}`);
        toast.success('Organization archived successfully!');
        fetchOrganizations();
      } catch (error) {
        console.error('Error archiving organization:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Failed to archive organization';
        toast.error(errorMessage);
      }
    }
  };

  const openEditModal = (organization) => {
    setSelectedOrganization(organization);
    setFormData({
      name: organization.name,
      email: organization.email,
      phone: organization.phone || '',
      website: organization.website || '',
      industry: organization.industry || '',
      plan: organization.plan,
      subscriptionStatus: organization.subscriptionStatus,
      billingEnabled: organization.billingEnabled,
      billingType: organization.billingType || 'recurring',
      autoPayEnabled: organization.autoPayEnabled !== false,
    });
    setShowEditModal(true);
  };

  const handleBillingTypeChange = async (orgEmail, billingType) => {
    try {
      await api.post('/api/v1/admin/organization/billing-type', { email: orgEmail, billingType });
      toast.success(`Billing type set to "${billingType}"`);
      fetchOrganizations();
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || error.response?.data?.message || error.message || 'Failed to update billing type';
      toast.error(errorMessage);
    }
  };

  const handleAutoPayToggle = async (orgEmail, enabled) => {
    try {
      await api.post('/api/v1/admin/organization/auto-pay', { email: orgEmail, enabled });
      toast.success(`Auto-pay ${enabled ? 'enabled' : 'disabled'}`);
      fetchOrganizations();
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || error.response?.data?.message || error.message || 'Failed to toggle auto-pay';
      toast.error(errorMessage);
    }
  };

  const handleCrmToggle = async (orgId, enabled) => {
    try {
      await api.post('/api/v1/admin/organization/crm-toggle', { orgId, enabled });
      toast.success(`CRM ${enabled ? 'enabled' : 'disabled'}`);
      setOrganizations(prev => prev.map(o => o._id === orgId ? { ...o, crmEnabled: enabled } : o));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to toggle CRM';
      toast.error(errorMessage);
    }
  };

  const handleStealthIdleReasonToggle = async (orgId, enabled) => {
    try {
      await api.post('/api/v1/admin/organization/stealth-idle-reason-toggle', { orgId, enabled });
      toast.success(`Stealth idle reasons ${enabled ? 'enabled' : 'disabled'}`);
      setOrganizations(prev => prev.map(o => o._id === orgId ? { ...o, stealthIdleReasonEnabled: enabled } : o));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to toggle stealth idle reasons';
      toast.error(errorMessage);
    }
  };

  // ADDITIVE: per-org stealth project attribution. ON lets that org's stealth-agent
  // users pick the project their background tracking is attributed to. OFF hides the
  // control and stops stamping new reports, but keeps every saved selection.
  const handleStealthProjectSelectToggle = async (orgId, enabled) => {
    try {
      await api.post('/api/v1/admin/organization/stealth-project-select-toggle', { orgId, enabled });
      toast.success(`Stealth project selection ${enabled ? 'enabled' : 'disabled'}`);
      setOrganizations(prev => prev.map(o => o._id === orgId ? { ...o, stealthProjectSelectEnabled: enabled } : o));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to toggle stealth project selection';
      toast.error(errorMessage);
    }
  };

  // ADDITIVE: per-org stealth shift clock. ON gives that org's stealth-agent users a
  // Start/End shift control + day timeline, and org admins a read-only timeline page.
  // OFF hides the UI and refuses the endpoints but PRESERVES every recorded event.
  // Records timestamps only — never affects tracking or any hours figure.
  const handleStealthClockToggle = async (orgId, enabled) => {
    try {
      await api.post('/api/v1/admin/organization/stealth-clock-toggle', { orgId, enabled });
      toast.success(`Stealth shift clock ${enabled ? 'enabled' : 'disabled'}`);
      setOrganizations(prev => prev.map(o => o._id === orgId ? { ...o, stealthClockEnabled: enabled } : o));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to toggle stealth shift clock';
      toast.error(errorMessage);
    }
  };

  // ADDITIVE: per-org Tasks module toggle. ON = org sees /dashboard/tasks and the
  // /api/v1/tasks module answers; OFF = UI hidden + endpoints 404, data preserved.
  const handleTasksToggle = async (orgId, enabled) => {
    try {
      await api.post('/api/v1/admin/organization/tasks-toggle', { orgId, enabled });
      toast.success(`Tasks module ${enabled ? 'enabled' : 'disabled'}`);
      setOrganizations(prev => prev.map(o => o._id === orgId ? { ...o, tasksEnabled: enabled } : o));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to toggle Tasks module';
      toast.error(errorMessage);
    }
  };

  const handleWorkOverviewToggle = async (orgId, enabled) => {
    try {
      await api.post('/api/v1/admin/organization/work-overview-toggle', { orgId, enabled });
      toast.success(`Work Overview ${enabled ? 'enabled' : 'disabled'}`);
      setOrganizations(prev => prev.map(o => o._id === orgId ? { ...o, workOverviewEnabled: enabled } : o));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to toggle Work Overview';
      toast.error(errorMessage);
    }
  };

  const handleEmployeeActivityToggle = async (orgId, enabled) => {
    try {
      await api.post('/api/v1/admin/organization/employee-activity-toggle', { orgId, enabled });
      toast.success(`Employee Activity ${enabled ? 'enabled' : 'disabled'}`);
      setOrganizations(prev => prev.map(o => o._id === orgId ? { ...o, employeeActivityEnabled: enabled } : o));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to toggle Employee Activity';
      toast.error(errorMessage);
    }
  };

  // ADDITIVE: MCP Server per-org toggle. Uses the standalone MCP admin route
  // (POST /api/v1/organizations/:id/mcp) — NOT admin.js. Enabling does NOT issue a
  // key; the org admin generates their own from Settings. The returned `mcp`
  // summary (never a key/hash) is merged into local org state so the toggle +
  // status reflect immediately.
  const handleMcpToggle = async (orgId, enabled) => {
    try {
      const res = await api.post(`/api/v1/organizations/${orgId}/mcp`, { enabled });
      const mcp = res?.data?.data?.mcp || { enabled };
      toast.success(`MCP ${enabled ? 'enabled' : 'disabled'}`);
      setOrganizations(prev => prev.map(o => o._id === orgId ? { ...o, mcp } : o));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to toggle MCP';
      toast.error(errorMessage);
    }
  };

  const getOrganizationStats = (orgId) => {
    const orgUsers = organizations.find(org => org._id === orgId)?.users || [];
    return {
      totalUsers: orgUsers.length,
      paidUsers: orgUsers.filter(u => u.billingEnabled).length,
      activeUsers: orgUsers.filter(u => u.isActive).length,
    };
  };

  const filteredOrganizations = organizations.filter(org => {
    const matchesSearch = org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      org.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || org.subscriptionStatus === statusFilter;
    const matchesPlan = !planFilter || org.plan === planFilter;
    return matchesSearch && matchesStatus && matchesPlan;
  });

  // Pagination logic
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedOrganizations = filteredOrganizations.slice(startIndex, endIndex);
  const totalOrganizations = filteredOrganizations.length;

  // Handle bulk delete
  const handleBulkDelete = () => {
    if (selectedRowKeys.length === 0) {
      toast.error('Please select at least one organization to delete');
      return;
    }

    const count = selectedRowKeys.length;
    const organizationText = count === 1 ? 'organization' : 'organizations';

    Modal.confirm({
      title: (
        <span style={{ color: isDark ? '#ffffff' : '#000000', fontSize: '18px', fontWeight: 600 }}>
          Confirm Bulk Delete
        </span>
      ),
      content: (
        <div style={{ color: isDark ? '#e5e7eb' : '#374151', fontSize: '14px', lineHeight: '1.6' }}>
          <p style={{ marginBottom: '8px' }}>
            Are you sure you want to permanently delete <strong style={{ color: isDark ? '#ffffff' : '#000000' }}>{count}</strong> {organizationText}?
          </p>
          <p style={{ color: isDark ? '#ef4444' : '#dc2626', fontWeight: 500 }}>
            ⚠️ This action cannot be undone.
          </p>
        </div>
      ),
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      width: 500,
      style: {
        top: '20%',
      },
      className: isDark ? 'dark-modal' : '',
      onOk: async () => {
        try {
          setLoading(true);
          const response = await api.delete('/api/v1/admin/organizations/bulk', {
            data: { organizationIds: selectedRowKeys }
          });
          
          if (response.data.success) {
            toast.success(`Successfully deleted ${response.data.data.successful} organization(s)`);
            if (response.data.data.failed > 0) {
              toast.error(`Failed to delete ${response.data.data.failed} organization(s)`);
            }
            setSelectedRowKeys([]);
            fetchOrganizations();
          }
        } catch (error) {
          console.error('Error bulk deleting organizations:', error);
          const errorMessage = error.response?.data?.message || error.message || 'Failed to delete organizations';
          toast.error(errorMessage);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // Row selection configuration
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => {
      setSelectedRowKeys(selectedKeys);
    },
    getCheckboxProps: (record) => ({
      name: record.name,
    }),
  };

  if (user?.role !== 'main_admin') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-gray-400">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

    const tableStyles = getTableStyles(isDark);
    const adminDesignSystem = getAdminDesignSystem(isDark);

  return (
    <>
      <style>{tableStyles}</style>
      <style>{adminDesignSystem}</style>
    <AdminThemeProvider>
    <div className={`${ADMIN_ROOT} h-screen flex overflow-hidden bg-black`}>
      <div className="tg-shell">
      {/* Shared sidebar (identical on every admin page) */}
      <AdminSidebar
        activeKey={activeTab}
        onSelectTab={(key) => {
          if (key === 'blogs') resetBlogPagination();
          setActiveTab(key);
        }}
      />

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        <main className="flex-1 relative z-0 overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="w-full px-5 sm:px-6">
              {/* Header */}
              {activeTab !== 'newsletter' && (
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h1 className="text-2xl font-bold text-white">Main Admin Panel</h1>
                  <p className="text-gray-400">System-wide management and monitoring</p>
                </div>
              </div>
              )}



              {/* Conditional Content Based on Active Tab */}
              {activeTab === 'organizations' ? (
                <>
                  <McpPlatformKeyCard />
                  {/* Organizations Stats Overview — pastel KPI cards */}
                  <KpiStrip
                    items={[
                      { label: 'Total Organizations', value: organizations.length, sub: 'all accounts' },
                      { label: 'Active Organizations', value: organizations.filter(org => org.subscriptionStatus === 'active').length, sub: 'paying / active' },
                      { label: 'Total Users', value: organizations.reduce((sum, org) => sum + (org.users?.length || 0), 0), sub: 'across all orgs' },
                      { label: 'Active Users Today', value: adminStats.activeUsersToday || 0, sub: 'today' },
                    ]}
                  />

                  {/* Search, Filters, and primary actions */}
                  <div className="flex items-start gap-3 mb-2 flex-wrap">
                    <div className="flex-1 min-w-[260px]">
                      <AdminToolbar
                        searchValue={searchTerm}
                        onSearch={(v) => { setSearchTerm(v); setCurrentPage(1); setSelectedRowKeys([]); }}
                        searchPlaceholder="Search organizations…"
                        filters={[
                          {
                            value: statusFilter,
                            onChange: (v) => { setStatusFilter(v || ''); setCurrentPage(1); setSelectedRowKeys([]); },
                            placeholder: 'All Status',
                            options: statuses.map((s) => ({ value: s.value, label: s.label })),
                          },
                          {
                            value: planFilter,
                            onChange: (v) => { setPlanFilter(v || ''); setCurrentPage(1); setSelectedRowKeys([]); },
                            placeholder: 'All Plans',
                            options: plans.map((p) => ({ value: p.value, label: p.label })),
                          },
                        ]}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedRowKeys.length > 0 && (
                        <AntButton danger onClick={handleBulkDelete} icon={<Trash2 size={15} />}>
                          Delete ({selectedRowKeys.length})
                        </AntButton>
                      )}
                      <AntButton type="primary" onClick={() => setShowAddModal(true)} icon={<UserPlus size={16} />}>
                        Add Organization
                      </AntButton>
                    </div>
                  </div>

                  {/* Organizations Table */}
                  <div className={`${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'} border rounded-lg overflow-hidden`}>
                    <div className={`px-5 py-3.5 border-b ${isDark ? 'border-white/10' : 'border-gray-200'} flex items-center justify-between`}>
                      <h2 className={`text-[15px] font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>All Organizations</h2>
                      <span className="text-xs text-gray-400">{totalOrganizations} total</span>
                    </div>
                    {loading ? (
                      <div className="p-6">
                        <Skeleton active paragraph={{ rows: 8 }} />
                      </div>
                    ) : (
                      <>
                        <div>
                          <Table
                            rowSelection={{ ...rowSelection, columnWidth: 48 }}
                            dataSource={paginatedOrganizations}
                            rowKey="_id"
                            pagination={false}
                            size="middle"
                            scroll={{ x: 1440 }}
                            sticky
                            className="custom-table"
                          columns={[
                            {
                              title: <ColHeader icon={Building} tone={1}>Organization</ColHeader>,
                              dataIndex: 'name',
                              key: 'name',
                              width: 240,
                              render: (text, record) => (
                                <OrgCell name={text} email={record.email} />
                              ),
                            },
                            {
                              title: <ColHeader icon={Users} tone={4}>Members</ColHeader>,
                              key: 'teamMembers',
                              width: 120,
                              align: 'center',
                              render: (_, record) => {
                                const stats = getOrganizationStats(record._id);
                                return (
                                  <span className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.totalUsers}</span>
                                );
                              },
                            },
                            {
                              title: <ColHeader icon={Tag} tone={3}>Plan</ColHeader>,
                              dataIndex: 'plan',
                              key: 'plan',
                              width: 120,
                              render: (plan) => (
                                <span className="tg-plan">{plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : '—'}</span>
                              ),
                            },
                            {
                              title: <ColHeader icon={Activity} tone={5}>Status</ColHeader>,
                              dataIndex: 'subscriptionStatus',
                              key: 'subscriptionStatus',
                              width: 140,
                              render: (status) => {
                                const { tone, label } = statusTone(status);
                                return <StatusBadge tone={tone}>{label}</StatusBadge>;
                              },
                            },
                            {
                              title: <ColHeader icon={Clock} tone={2}>Joined</ColHeader>,
                              dataIndex: 'createdAt',
                              key: 'createdAt',
                              width: 120,
                              render: (date) => (
                                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {new Date(date).toLocaleDateString()}
                                </span>
                              ),
                            },
                            {
                              title: 'Billing',
                              key: 'billingType',
                              width: 150,
                              render: (_, record) => {
                                const isPaid = ['growth', 'enterprise'].includes(record.plan);
                                const isActive = record.subscriptionStatus === 'active';
                                if (!isPaid || !isActive) {
                                  return <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>—</span>;
                                }
                                const bt = record.billingType || 'recurring';
                                const ap = record.autoPayEnabled !== false;
                                return (
                                  <span className="tg-bill">
                                    {bt === 'one_time' ? 'One-time' : 'Recurring'}
                                    {bt === 'recurring' && (
                                      <small className={ap ? '' : 'tg-bill-warn'}>· {ap ? 'Auto' : 'Manual'}</small>
                                    )}
                                  </span>
                                );
                              },
                            },
                            {
                              title: 'CRM',
                              key: 'crmEnabled',
                              width: 80,
                              align: 'center',
                              render: (_, record) => (
                                <button
                                  onClick={() => handleCrmToggle(record._id, !record.crmEnabled)}
                                  className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                                    record.crmEnabled
                                      ? 'bg-green-500'
                                      : (isDark ? 'bg-gray-600' : 'bg-gray-300')
                                  }`}
                                  title={record.crmEnabled ? 'CRM enabled — click to disable' : 'CRM disabled — click to enable'}
                                >
                                  <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ${record.crmEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
                                </button>
                              ),
                            },
                            {
                              title: 'Idle',
                              key: 'stealthIdleReasonEnabled',
                              width: 80,
                              align: 'center',
                              render: (_, record) => (
                                <button
                                  onClick={() => handleStealthIdleReasonToggle(record._id, !record.stealthIdleReasonEnabled)}
                                  className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                                    record.stealthIdleReasonEnabled
                                      ? 'bg-green-500'
                                      : (isDark ? 'bg-gray-600' : 'bg-gray-300')
                                  }`}
                                  title={record.stealthIdleReasonEnabled
                                    ? 'Stealth idle reasons enabled — click to disable'
                                    : 'Stealth idle reasons disabled — click to enable'}
                                >
                                  <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ${record.stealthIdleReasonEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
                                </button>
                              ),
                            },
                            {
                              // ADDITIVE: stealth project attribution toggle — mirrors the Idle toggle.
                              // When on, that org's stealth-agent users can pick which project their
                              // background tracking is attributed to. Off = hidden + no new stamping,
                              // but saved selections are preserved.
                              title: 'Proj',
                              key: 'stealthProjectSelectEnabled',
                              width: 80,
                              align: 'center',
                              render: (_, record) => (
                                <button
                                  onClick={() => handleStealthProjectSelectToggle(record._id, !record.stealthProjectSelectEnabled)}
                                  className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                                    record.stealthProjectSelectEnabled
                                      ? 'bg-green-500'
                                      : (isDark ? 'bg-gray-600' : 'bg-gray-300')
                                  }`}
                                  title={record.stealthProjectSelectEnabled
                                    ? 'Stealth project selection enabled — click to disable'
                                    : 'Stealth project selection disabled — click to enable'}
                                >
                                  <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ${record.stealthProjectSelectEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
                                </button>
                              ),
                            },
                            {
                              // ADDITIVE: stealth shift clock toggle — mirrors the Proj/Idle toggles.
                              // When on, that org's stealth-agent users get a Start/End shift control
                              // and org admins get a read-only shift timeline. Off = UI hidden +
                              // endpoints refused, but every recorded event is preserved.
                              // Records timestamps only — tracking and all hours figures are
                              // untouched in BOTH states.
                              title: 'Clock',
                              key: 'stealthClockEnabled',
                              width: 80,
                              align: 'center',
                              render: (_, record) => (
                                <button
                                  onClick={() => handleStealthClockToggle(record._id, !record.stealthClockEnabled)}
                                  className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                                    record.stealthClockEnabled
                                      ? 'bg-green-500'
                                      : (isDark ? 'bg-gray-600' : 'bg-gray-300')
                                  }`}
                                  title={record.stealthClockEnabled
                                    ? 'Stealth shift clock enabled — click to disable'
                                    : 'Stealth shift clock disabled — click to enable'}
                                >
                                  <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ${record.stealthClockEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
                                </button>
                              ),
                            },
                            {
                              // ADDITIVE: Tasks module toggle — mirrors the Clock/Activity toggles.
                              // ON = org gets /dashboard/tasks + /api/v1/tasks; OFF = hidden, data kept.
                              title: 'Tasks',
                              key: 'tasksEnabled',
                              width: 80,
                              align: 'center',
                              render: (_, record) => (
                                <button
                                  onClick={() => handleTasksToggle(record._id, !record.tasksEnabled)}
                                  className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                                    record.tasksEnabled
                                      ? 'bg-green-500'
                                      : (isDark ? 'bg-gray-600' : 'bg-gray-300')
                                  }`}
                                  title={record.tasksEnabled
                                    ? 'Tasks module enabled — click to disable'
                                    : 'Tasks module disabled — click to enable'}
                                >
                                  <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ${record.tasksEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
                                </button>
                              ),
                            },
                            {
                              title: 'Overview',
                              key: 'workOverviewEnabled',
                              width: 90,
                              align: 'center',
                              render: (_, record) => (
                                <button
                                  onClick={() => handleWorkOverviewToggle(record._id, !record.workOverviewEnabled)}
                                  className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                                    record.workOverviewEnabled
                                      ? 'bg-green-500'
                                      : (isDark ? 'bg-gray-600' : 'bg-gray-300')
                                  }`}
                                  title={record.workOverviewEnabled
                                    ? 'Work Overview enabled — click to disable'
                                    : 'Work Overview disabled — click to enable'}
                                >
                                  <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ${record.workOverviewEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
                                </button>
                              ),
                            },
                            {
                              // ADDITIVE: Employee Activity page toggle — mirrors the CRM/Idle toggles.
                              // When on, org admins see a read-only Employee/Status/Activity page.
                              title: 'Activity',
                              key: 'employeeActivityEnabled',
                              width: 80,
                              align: 'center',
                              render: (_, record) => (
                                <button
                                  onClick={() => handleEmployeeActivityToggle(record._id, !record.employeeActivityEnabled)}
                                  className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                                    record.employeeActivityEnabled
                                      ? 'bg-green-500'
                                      : (isDark ? 'bg-gray-600' : 'bg-gray-300')
                                  }`}
                                  title={record.employeeActivityEnabled
                                    ? 'Employee Activity enabled — click to disable'
                                    : 'Employee Activity disabled — click to enable'}
                                >
                                  <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ${record.employeeActivityEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
                                </button>
                              ),
                            },
                            {
                              // ADDITIVE: MCP Server toggle — mirrors the CRM/Idle toggles.
                              // Shows enabled/disabled; when a key exists, surfaces
                              // last-used in the tooltip. Enabling here only flips the
                              // gate — the org admin generates their own key in Settings.
                              title: 'MCP',
                              key: 'mcp',
                              width: 110,
                              render: (_, record) => {
                                const mcp = record.mcp || {};
                                const on = mcp.enabled === true;
                                const lastUsed = mcp.lastUsedAt
                                  ? `last used ${new Date(mcp.lastUsedAt).toLocaleDateString()}`
                                  : (mcp.hasKey ? 'key generated, not used yet' : 'no key generated');
                                return (
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => handleMcpToggle(record._id, !on)}
                                      className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                                        on ? 'bg-green-500' : (isDark ? 'bg-gray-600' : 'bg-gray-300')
                                      }`}
                                      title={on
                                        ? `MCP enabled (${lastUsed}) — click to disable`
                                        : 'MCP disabled — click to enable'}
                                    >
                                      <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ${on ? 'translate-x-4' : 'translate-x-0'}`} />
                                    </button>
                                    {on && (
                                      <span className={`text-[10px] leading-tight ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {mcp.hasKey ? 'key ✓' : 'no key'}
                                      </span>
                                    )}
                                  </div>
                                );
                              },
                            },
                            {
                              title: 'API Hits',
                              key: 'streamApiUsage',
                              width: 100,
                              align: 'center',
                              render: (_, record) => {
                                const totalRequests = record.streamApiUsage?.totalRequests || 0;
                                return (
                                  <span className={`text-sm tabular-nums ${totalRequests > 0 ? (isDark ? 'text-white' : 'text-gray-900') : (isDark ? 'text-gray-500' : 'text-gray-400')}`}>
                                    {totalRequests.toLocaleString()}
                                  </span>
                                );
                              },
                            },
                            {
                              title: 'Actions',
                              key: 'actions',
                              width: 130,
                              fixed: 'right',
                              align: 'center',
                              render: (_, record) => (
                                  <div className="tg-actions flex items-center justify-center gap-0.5">
                                    <button
                                      onClick={() => switchToOrganization(record._id)}
                                      className="tg-act disabled:opacity-40 disabled:cursor-not-allowed"
                                      title={switchingOrgId === record._id ? 'Switching...' : 'Switch to Organization'}
                                      disabled={switchingOrgId === record._id}
                                    >
                                      <ArrowRightLeft size={15} className={switchingOrgId === record._id ? 'animate-switch-flip' : ''} />
                                    </button>
                                    <button
                                      onClick={() => openEditModal(record)}
                                      className="tg-act"
                                      title="Edit Organization"
                                    >
                                      <Edit size={15} />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteOrganization(record._id)}
                                      className="tg-act tg-act-danger"
                                      title="Archive Organization"
                                    >
                                      <Trash2 size={15} />
                                    </button>
                                  </div>
                              ),
                            },
                          ]}
                          />
                          <div className={`px-6 py-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'} flex justify-end`}>
                            <Pagination
                              current={currentPage}
                              pageSize={pageSize}
                              total={totalOrganizations}
                              onChange={(page, size) => {
                                setCurrentPage(page);
                                setPageSize(size);
                                setSelectedRowKeys([]);
                              }}
                              onShowSizeChange={(current, size) => {
                                setCurrentPage(1);
                                setPageSize(size);
                                setSelectedRowKeys([]);
                              }}
                              showSizeChanger
                              showTotal={(total, range) => (
                                <span className={isDark ? 'text-white' : 'text-gray-700'}>
                                  {range[0]}-{range[1]} of {total} organizations
                                </span>
                              )}
                              pageSizeOptions={['10', '20', '50', '100']}
                            />
                    </div>
                        </div>
                      </>
                    )}
                  </div>
                </>
              ) : activeTab === 'scheduler' ? (
                <>
                  {/* Scheduler Logs Stats Overview */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white/5 p-6 rounded-lg border border-white/10">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-400 text-sm">Total Runs</p>
                          <p className="text-2xl font-bold text-white">{schedulerStats.stats?.totalRuns || 0}</p>
                        </div>
                        <Activity className="h-8 w-8 text-blue-400" />
                      </div>
                    </div>
                    <div className="bg-white/5 p-6 rounded-lg border border-white/10">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-400 text-sm">Total Users Analyzed</p>
                          <p className="text-2xl font-bold text-white">{schedulerStats.stats?.totalUsers || 0}</p>
                        </div>
                        <Users className="h-8 w-8 text-green-400" />
                      </div>
                    </div>
                    <div className="bg-white/5 p-6 rounded-lg border border-white/10">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-400 text-sm">Total Screenshots</p>
                          <p className="text-2xl font-bold text-white">{schedulerStats.stats?.totalScreenshots || 0}</p>
                        </div>
                        <BarChart3 className="h-8 w-8 text-yellow-400" />
                      </div>
                    </div>
                    <div className="bg-white/5 p-6 rounded-lg border border-white/10">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-400 text-sm">Avg Processing Time</p>
                          <p className="text-2xl font-bold text-white">
                            {schedulerStats.stats?.avgProcessingTime ?
                              `${Math.round(schedulerStats.stats.avgProcessingTime)}ms` : '0ms'}
                          </p>
                        </div>
                        <Clock className="h-8 w-8 text-purple-400" />
                      </div>
                    </div>
                  </div>

                  {/* Scheduler Logs Table */}
                  <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden">
                    <div className="px-6 py-4 border-b border-white/10">
                      <h2 className="text-lg font-semibold text-white">AI Analysis Scheduler Logs</h2>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-white/5">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                              Trigger Time
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                              Type
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                              Organization
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                              Users
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                              Screenshots
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                              Processing Time
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                              Success Rate
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white/5 divide-y divide-white/10">
                          {schedulerLoading ? (
                            <tr>
                              <td colSpan="8" className="px-6 py-4 text-center">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                              </td>
                            </tr>
                          ) : schedulerLogs.length === 0 ? (
                            <tr>
                              <td colSpan="8" className="px-6 py-4 text-center text-gray-400">
                                No scheduler logs found
                              </td>
                            </tr>
                          ) : (
                            schedulerLogs.map((log) => (
                              <tr key={log._id} className="hover:bg-white/10">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div>
                                    <div className="text-sm font-medium text-white">
                                      {new Date(log.triggerTime).toLocaleDateString()}
                                    </div>
                                    <div className="text-sm text-gray-400">
                                      {new Date(log.triggerTime).toLocaleTimeString()}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${log.triggerType === 'manual' ? 'bg-blue-500/20 text-blue-400' :
                                    log.triggerType === 'automatic' ? 'bg-green-500/20 text-green-400' :
                                      'bg-yellow-500/20 text-yellow-400'
                                    }`}>
                                    {log.triggerType}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-white">{log.organizationName}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-white">{log.totalUsers}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-white">{log.totalScreenshots}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${log.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                                    log.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                                      log.status === 'partial' ? 'bg-yellow-500/20 text-yellow-400' :
                                        'bg-blue-500/20 text-blue-400'
                                    }`}>
                                    {log.status}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-white">
                                    {log.totalProcessingTime ? `${Math.round(log.totalProcessingTime)}ms` : '0ms'}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-white">
                                    {log.totalUsers > 0 ? `${Math.round((log.successfulAnalysis / log.totalUsers) * 100)}%` : '0%'}
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              ) : activeTab === 'ai-prompts' ? (
                <>
                  {/* AI Prompts Stats Overview */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white/5 p-6 rounded-lg border border-white/10">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-400 text-sm">Total Prompts</p>
                          <p className="text-2xl font-bold text-white">{aiPrompts.length}</p>
                        </div>
                        <Brain className="h-8 w-8 text-blue-400" />
                      </div>
                    </div>
                    <div className="bg-white/5 p-6 rounded-lg border border-white/10">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-400 text-sm">Active Prompts</p>
                          <p className="text-2xl font-bold text-white">
                            {aiPrompts.filter(prompt => prompt.isActive).length}
                          </p>
                        </div>
                        <Eye className="h-8 w-8 text-green-400" />
                      </div>
                    </div>
                    <div className="bg-white/5 p-6 rounded-lg border border-white/10">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-400 text-sm">Hourly Prompts</p>
                          <p className="text-2xl font-bold text-white">
                            {aiPrompts.filter(prompt => prompt.promptType === 'hourly').length}
                          </p>
                        </div>
                        <Clock className="h-8 w-8 text-yellow-400" />
                      </div>
                    </div>
                    <div className="bg-white/5 p-6 rounded-lg border border-white/10">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-400 text-sm">Total Usage</p>
                          <p className="text-2xl font-bold text-white">
                            {aiPrompts.reduce((sum, prompt) => sum + (prompt.usageCount || 0), 0)}
                          </p>
                        </div>
                        <BarChart3 className="h-8 w-8 text-purple-400" />
                      </div>
                    </div>
                  </div>

                  {/* Add Prompt Button */}
                  <div className="mb-6">
                    <button
                      onClick={() => setShowPromptModal(true)}
                      className="bg-blue-600 !text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors"
                    >
                      <Brain size={20} />
                      Add AI Prompt
                    </button>
                  </div>

                  {/* AI Prompts Table */}
                  <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden">
                    <div className="px-6 py-4 border-b border-white/10">
                      <h2 className="text-lg font-semibold text-white">AI Prompts Management</h2>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-white/5">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                              Title
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                              Type
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                              Model
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                              Usage Count
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                              Last Used
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                              Created
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white/5 divide-y divide-white/10">
                          {promptsLoading ? (
                            <tr>
                              <td colSpan="8" className="px-6 py-4 text-center">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                              </td>
                            </tr>
                          ) : aiPrompts.length === 0 ? (
                            <tr>
                              <td colSpan="8" className="px-6 py-4 text-center text-gray-400">
                                No AI prompts found
                              </td>
                            </tr>
                          ) : (
                            aiPrompts.map((prompt) => (
                              <tr key={prompt._id} className="hover:bg-white/10">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div>
                                    <div className="text-sm font-medium text-white">{prompt.title}</div>
                                    <div className="text-sm text-gray-400">{prompt.description || 'No description'}</div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    prompt.promptType === 'hourly' ? 'bg-blue-500/20 text-blue-400' :
                                    prompt.promptType === 'daily' ? 'bg-green-500/20 text-green-400' :
                                    'bg-purple-500/20 text-purple-400'
                                  }`}>
                                    {prompt.promptType}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-white">{prompt.gptModel || 'gpt-3.5-turbo'}</div>
                                  <div className="text-xs text-gray-400">Max: {getMaxTokensForModel(prompt.gptModel || 'gpt-3.5-turbo')} tokens</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    prompt.isActive ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                                  }`}>
                                    {prompt.isActive ? 'Active' : 'Inactive'}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-white">{prompt.usageCount || 0}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-400">
                                    {prompt.lastUsedAt ? new Date(prompt.lastUsedAt).toLocaleDateString() : 'Never'}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-400">
                                    {new Date(prompt.createdAt).toLocaleDateString()}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                  <div className="flex items-center space-x-2">
                                    <button
                                      onClick={() => openPromptEditModal(prompt)}
                                      className="text-gray-400 hover:text-white transition-colors"
                                      title="Edit Prompt"
                                    >
                                      <Edit size={16} />
                                    </button>
                                    <button
                                      onClick={() => handleDeletePrompt(prompt._id)}
                                      className="text-red-400 hover:text-red-300 transition-colors"
                                      title="Delete Prompt"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              ) : activeTab === 'system-logics' ? (
                <>
                  {/* System Critical Logics Documentation */}
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h2 className="text-2xl font-bold text-white">System Critical Logics</h2>
                        <p className="text-gray-400">Core function documentation and formulas for easy reference</p>
                      </div>
                    </div>

                    <div className="grid gap-6">
                      {/* TimeConversion Function */}
                      <div className="bg-white/5 border border-white/10 rounded-lg p-6">
                        <div className="flex items-center mb-4">
                          <Clock className="h-6 w-6 text-blue-400 mr-3" />
                          <h3 className="text-xl font-semibold text-white">TimeConversion Function</h3>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-300 mb-2">Purpose:</h4>
                            <p className="text-gray-400">Dual timezone storage - UTC for data integrity, Organization timezone for user display</p>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-300 mb-2">Implementation:</h4>
                            <div className="bg-black/30 p-3 rounded border border-white/10">
                              <code className="text-green-400 text-sm">
                                1. Screenshots: timestamp (UTC) + localTime (Org timezone)<br/>
                                2. Sessions: startTime (UTC) + startTimeLocal (Org timezone)<br/>
                                3. HourlySummary: hourSlot (UTC) + hourSlotLocal (Org timezone)<br/>
                                4. DailySummary: date (UTC) + dateLocal (Org timezone)<br/>
                                5. Pre-save middleware: Auto-calculates local times<br/>
                                6. Display: Uses Organization.settings.timezone only
                              </code>
                            </div>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-300 mb-2">Live Example:</h4>
                            <p className="text-gray-400">
                              UTC "10:20" → IST "15:50" (Asia/Kolkata +5:30)<br/>
                              Storage: Both times saved automatically<br/>
                              Display: Organization timezone only
                            </p>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-300 mb-2">Benefits:</h4>
                            <div className="text-gray-400 text-sm space-y-1">
                              <div>• Data integrity: UTC storage never changes</div>
                              <div>• User experience: Local time display</div>
                              <div>• Team consistency: All org users see same times</div>
                              <div>• Auto-calculation: No manual conversion needed</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Timezone Implementation Status */}
                      <div className="bg-white/5 border border-white/10 rounded-lg p-6">
                        <div className="flex items-center mb-4">
                          <Globe className="h-6 w-6 text-green-400 mr-3" />
                          <h3 className="text-xl font-semibold text-white">Timezone Implementation Status</h3>
                          <span className="ml-auto px-2 py-1 text-xs bg-green-500/20 text-green-400 rounded-full">ACTIVE</span>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <h4 className="text-sm font-semibold text-gray-300">Database Tables:</h4>
                            <div className="text-xs text-gray-400 space-y-1">
                              <div className="flex justify-between">
                                <span>Screenshots.localTime</span>
                                <span className="text-green-400">✅ Active</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Sessions.startTimeLocal</span>
                                <span className="text-green-400">✅ Active</span>
                              </div>
                              <div className="flex justify-between">
                                <span>HourlySummary.hourSlotLocal</span>
                                <span className="text-green-400">✅ Active</span>
                              </div>
                              <div className="flex justify-between">
                                <span>DailySummary.dateLocal</span>
                                <span className="text-green-400">✅ Active</span>
                              </div>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <h4 className="text-sm font-semibold text-gray-300">Current Settings:</h4>
                            <div className="text-xs text-gray-400 space-y-1">
                              <div>Organization Timezone: <span className="text-white">Asia/Kolkata</span></div>
                              <div>UTC Offset: <span className="text-white">+05:30</span></div>
                              <div>Display Format: <span className="text-white">IST</span></div>
                              <div>Auto-calculation: <span className="text-green-400">Enabled</span></div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* AnalysisPro Statistical Function */}
                      <div className="bg-white/5 border border-white/10 rounded-lg p-6">
                        <div className="flex items-center mb-4">
                          <BarChart3 className="h-6 w-6 text-blue-400 mr-3" />
                          <h3 className="text-xl font-semibold text-white">AnalysisPro Statistical Function</h3>
                          <span className="ml-auto px-2 py-1 text-xs bg-blue-500/20 text-blue-400 rounded-full">PURE STATS</span>
                        </div>
                        <div className="space-y-4">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-300 mb-2">Purpose:</h4>
                            <p className="text-gray-400">Real-time statistical analysis engine that processes raw screenshot data without AI dependencies</p>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-300 mb-2">New Time Calculation Logic:</h4>
                            <div className="bg-black/30 p-3 rounded border border-white/10">
                              <code className="text-green-400 text-sm">
                                1. <strong>Tracked Time</strong> = Sum of intervals between consecutive screenshots<br/>
                                2. <strong>Idle Detection</strong> = Consecutive screenshots with inputActivity=0 AND mouseActivity=0 for ≥10min<br/>
                                3. <strong>Active Time</strong> = Tracked Time - Idle Time<br/>
                                4. <strong>Session Hours</strong> = TrackingSession.totalSessionTime ÷ 60 (rounded to 2 decimals)
                              </code>
                            </div>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-300 mb-2">Example Calculation:</h4>
                            <div className="bg-black/30 p-3 rounded border border-white/10">
                              <code className="text-yellow-400 text-sm">
                                📸 9 screenshots: 1:00, 1:03, 1:06, 1:09, 1:12, 1:15, 1:18, 1:21, 1:24<br/>
                                ⏱️ Tracked Time: 3+3+3+3+3+3+3+3 = 24 minutes<br/>
                                😴 Idle Period: 1:09-1:21 (12 min) with no activity<br/>
                                🎯 Active Time: 24 - 12 = 12 minutes
                              </code>
                            </div>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-300 mb-2">Output Fields:</h4>
                            <div className="grid grid-cols-2 gap-2 text-xs text-gray-400">
                              <div>• trackedTime (NEW)</div>
                              <div>• activeMinutes (UPDATED)</div>
                              <div>• idleTime (UPDATED)</div>
                              <div>• screenshots</div>
                              <div>• inputActivity</div>
                              <div>• mouseActivity</div>
                              <div>• applications[]</div>
                              <div>• urls[]</div>
                              <div>• activityTimeline[]</div>
                              <div>• sessionData</div>
                              <div>• totalSessionHours</div>
                            </div>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-300 mb-2">Data Sources:</h4>
                            <div className="text-gray-400 text-sm space-y-1">
                              <div>• <strong>Screenshots table</strong>: Activity data, timestamps, input activity, mouse, apps, URLs</div>
                              <div>• <strong>TrackingSession table</strong>: Session durations and timing data</div>
                              <div>• <strong>No AI processing</strong>: Pure statistical calculations only</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Time Calculation Logic */}
                      <div className="bg-white/5 border border-white/10 rounded-lg p-6">
                        <div className="flex items-center mb-4">
                          <Clock className="h-6 w-6 text-green-400 mr-3" />
                          <h3 className="text-xl font-semibold text-white">Time Calculation Logic (Updated)</h3>
                          <span className="ml-auto px-2 py-1 text-xs bg-green-500/20 text-green-400 rounded-full">NEW LOGIC</span>
                        </div>
                        <div className="space-y-4">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-300 mb-2">Three-Tier Time Calculation:</h4>
                            <div className="bg-black/30 p-3 rounded border border-white/10">
                              <code className="text-green-400 text-sm">
                                1. <strong>Tracked Time</strong> = Sum of intervals between consecutive screenshots (using localTime)<br/>
                                2. <strong>Idle Time</strong> = Consecutive screenshots ≥10min with inputActivity=0 AND mouseActivity=0<br/>
                                3. <strong>Active Time</strong> = Tracked Time - Idle Time
                              </code>
                            </div>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-300 mb-2">Real Example (9 screenshots in 1 hour):</h4>
                            <div className="bg-black/30 p-3 rounded border border-white/10">
                              <code className="text-yellow-400 text-sm">
                                📸 Screenshots: 1:00 → 1:03 → 1:06 → 1:09 → 1:12 → 1:15 → 1:18 → 1:21 → 1:24<br/>
                                ⏱️ Intervals: 3+3+3+3+3+3+3+3 = 24 minutes tracked<br/>
                                😴 Idle: 1:09-1:21 (12 min) no input/mouse = 12 min idle<br/>
                                🎯 Result: Active=12min, Idle=12min, Tracked=24min
                              </code>
                            </div>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-300 mb-2">Application & URL Metrics:</h4>
                            <div className="bg-black/30 p-3 rounded border border-white/10">
                              <code className="text-green-400 text-sm">
                                1. Apps: Calculated from screenshot intervals with activity weighting<br/>
                                2. URLs: 3 minutes per screenshot appearance, grouped by domain<br/>
                                3. Sorting: By total usage time (highest first)
                              </code>
                            </div>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-300 mb-2">Key Changes:</h4>
                            <div className="text-gray-400 text-sm space-y-1">
                              <div>• ❌ <strong>Removed</strong>: AI analysis, productivity scoring, projects, goals</div>
                              <div>• ✅ <strong>Added</strong>: trackedTime field for precise interval tracking</div>
                              <div>• ✅ <strong>Updated</strong>: More accurate active/idle time calculation</div>
                              <div>• ✅ <strong>Focus</strong>: Pure statistical data from Screenshot table</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Daily Analysis Logic */}
                      <div className="bg-white/5 border border-white/10 rounded-lg p-6">
                        <div className="flex items-center mb-4">
                          <Activity className="h-6 w-6 text-orange-400 mr-3" />
                          <h3 className="text-xl font-semibold text-white">Daily Statistical Analysis</h3>
                          <span className="ml-auto px-2 py-1 text-xs bg-orange-500/20 text-orange-400 rounded-full">STATS ONLY</span>
                        </div>
                        <div className="space-y-4">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-300 mb-2">Daily Aggregation Logic:</h4>
                            <div className="bg-black/30 p-3 rounded border border-white/10">
                              <code className="text-green-400 text-sm">
                                1. <strong>Tracked Time</strong> = Sum of all screenshot intervals for the day<br/>
                                2. <strong>Active Time</strong> = Tracked Time - All idle periods ≥10min<br/>
                                3. <strong>Idle Time</strong> = Total consecutive idle periods detected<br/>
                                4. <strong>Session Hours</strong> = TrackingSession data converted to hours<br/>
                                5. <strong>Applications/URLs</strong> = Aggregated and sorted by usage time
                              </code>
                            </div>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-300 mb-2">Data Aggregation Sources:</h4>
                            <div className="text-gray-400 text-sm space-y-1">
                              <div>• <strong>Screenshot intervals</strong>: Precise time tracking based on actual capture times</div>
                              <div>• <strong>Activity detection</strong>: Input and mouse data from desktop app</div>
                              <div>• <strong>Session data</strong>: TrackingSession table for work session metrics</div>
                              <div>• <strong>Usage patterns</strong>: Application and URL usage aggregated from all screenshots</div>
                            </div>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-300 mb-2">Output Structure:</h4>
                            <div className="bg-black/30 p-3 rounded border border-white/10">
                              <code className="text-blue-400 text-sm">
                                📊 Daily Stats: trackedTime, activeMinutes, idleTime, totalSessionHours<br/>
                                📸 Activity: screenshots, inputActivity, mouseActivity<br/>
                                💻 Usage: applications[], urls[], activityTimeline[]<br/>
                                📈 Sessions: sessionData object with detailed metrics
                              </code>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Processing Logic */}
                      <div className="bg-white/5 border border-white/10 rounded-lg p-6">
                        <div className="flex items-center mb-4">
                          <Settings className="h-6 w-6 text-yellow-400 mr-3" />
                          <h3 className="text-xl font-semibold text-white">Processing & Trigger Logic</h3>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-300 mb-2">Duplicate Prevention:</h4>
                            <div className="bg-black/30 p-3 rounded border border-white/10">
                              <code className="text-green-400 text-sm">
                                1. Screenshot captured → OCR processing<br/>
                                2. After OCR completion → Check hour slot<br/>
                                3. If ANY screenshot in hour has status 0 → Process ALL screenshots in that hour<br/>
                                4. Generate ONE hourly analysis per hour slot<br/>
                                5. Mark ALL screenshots in hour as processed (status 1)
                              </code>
                            </div>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-300 mb-2">Data Flow:</h4>
                            <p className="text-gray-400">Screenshot → OCR → HourlySummary → DailySummary → Frontend Display</p>
                          </div>
                        </div>
                      </div>

                      {/* AnalysisPro API Response Structure */}
                      <div className="bg-white/5 border border-white/10 rounded-lg p-6">
                        <div className="flex items-center mb-4">
                          <Brain className="h-6 w-6 text-purple-400 mr-3" />
                          <h3 className="text-xl font-semibold text-white">AnalysisPro API Response Structure</h3>
                          <span className="ml-auto px-2 py-1 text-xs bg-purple-500/20 text-purple-400 rounded-full">LIVE API</span>
                        </div>
                        <div className="space-y-4">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response Fields (11 fields):</h4>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="bg-black/30 p-3 rounded border border-white/10">
                                <h5 className="text-xs font-semibold text-blue-300 mb-2">Time Metrics</h5>
                                <code className="text-green-400 text-xs">
                                  trackedTime: 642        // Sum of intervals<br/>
                                  activeMinutes: 642      // Tracked - Idle<br/>
                                  idleTime: 0            // Idle periods ≥10min<br/>
                                  totalSessionHours: 5.2  // Session time in hours
                                </code>
                              </div>
                              <div className="bg-black/30 p-3 rounded border border-white/10">
                                <h5 className="text-xs font-semibold text-yellow-300 mb-2">Activity Metrics</h5>
                                <code className="text-green-400 text-xs">
                                  screenshots: 141        // Screenshot count<br/>
                                  inputActivity: 776     // Input activity count<br/>
                                  mouseActivity: 462     // Total mouse actions<br/>
                                  applications: [Array]    // App usage array<br/>
                                  urls: [Array]           // URL usage array<br/>
                                  activityTimeline: [Array] // Timeline array<br/>
                                  sessionData: Object     // Session metrics
                                </code>
                              </div>
                            </div>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-300 mb-2">Key Formula Examples:</h4>
                            <div className="bg-black/30 p-3 rounded border border-white/10">
                              <code className="text-yellow-400 text-sm">
                                🕐 9 screenshots at 1:00, 1:03, 1:06, 1:09, 1:12, 1:15, 1:18, 1:21, 1:24<br/>
                                ⏱️ Tracked Time: 3+3+3+3+3+3+3+3 = 24 minutes<br/>
                                😴 Idle: 1:09-1:21 (12 min) with inputActivity=0, mouse=0<br/>
                                🎯 Active Time: 24 - 12 = 12 minutes<br/>
                                📊 Final: trackedTime=24, activeMinutes=12, idleTime=12
                              </code>
                            </div>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-300 mb-2">API Usage:</h4>
                            <div className="text-gray-400 text-sm space-y-1">
                              <div>• <strong>Endpoint</strong>: Called via /api/v1/analysis-pro routes</div>
                              <div>• <strong>Real-time</strong>: No caching, always fresh calculation from Screenshot table</div>
                              <div>• <strong>Performance</strong>: Optimized with indexed queries and lean() operations</div>
                              <div>• <strong>No AI</strong>: Pure statistical analysis, no OpenAI dependencies</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : activeTab === 'blogs' ? (
                <>
                  {/* Blog Management */}
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Blog Management</h2>
                        <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Create and manage blog posts for all organizations</p>
                      </div>
                      <button
                        onClick={() => {
                          resetBlogForm();
                          setShowBlogModal(true);
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-[#00D1C1] !text-white font-semibold rounded-lg hover:bg-[#00D1C1]/90 transition-all"
                      >
                        <Plus className="w-5 h-5" />
                        Create New Blog
                      </button>
                    </div>

                    {/* Blog Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                      <div className="bg-white/5 p-6 rounded-lg border border-white/10">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-gray-400 text-sm">Total Blogs</p>
                            <p className="text-2xl font-bold text-white">{blogs.length}</p>
                          </div>
                          <FileText className="h-8 w-8 text-blue-400" />
                        </div>
                      </div>
                      <div className="bg-white/5 p-6 rounded-lg border border-white/10">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-gray-400 text-sm">Live Blogs</p>
                            <p className="text-2xl font-bold text-white">
                              {blogs.filter(b => b.isLive).length}
                            </p>
                          </div>
                          <CheckCircle className="h-8 w-8 text-green-400" />
                        </div>
                      </div>
                      <div className="bg-white/5 p-6 rounded-lg border border-white/10">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-gray-400 text-sm">Draft Blogs</p>
                            <p className="text-2xl font-bold text-white">
                              {blogs.filter(b => !b.isLive).length}
                            </p>
                          </div>
                          <EyeOff className="h-8 w-8 text-yellow-400" />
                        </div>
                      </div>
                      <div className="bg-white/5 p-6 rounded-lg border border-white/10">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-gray-400 text-sm">Total Views</p>
                            <p className="text-2xl font-bold text-white">
                              {blogs.reduce((sum, b) => sum + (b.views || 0), 0)}
                            </p>
                          </div>
                          <Eye className="h-8 w-8 text-purple-400" />
                        </div>
                      </div>
                    </div>

                    {/* Blog List */}
                    {blogsLoading ? (
                      <div className="space-y-4">
                        {[1, 2, 3].map((i) => (
                          <Skeleton key={i} active paragraph={{ rows: 2 }} />
                        ))}
                      </div>
                    ) : blogs.length === 0 ? (
                      <div className={`text-center py-12 ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-lg`}>
                        <FileText className={`w-16 h-16 ${isDark ? 'text-gray-400' : 'text-gray-500'} mx-auto mb-4`} />
                        <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>No blogs found. Create your first blog post!</p>
                      </div>
                    ) : (
                      <div className={`${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'} border rounded-lg overflow-hidden`}>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className={`${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'} border-b`}>
                              <tr>
                                <th className={`px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wider max-w-xs`}>Title</th>
                                <th className={`px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wider`}>Category</th>
                                <th className={`px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wider`}>Author</th>
                                <th className={`px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wider`}>Status</th>
                                <th className={`px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wider`}>Views</th>
                                <th className={`px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wider`}>Created</th>
                                <th className={`px-6 py-3 text-right text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wider`}>Actions</th>
                              </tr>
                            </thead>
                            <tbody className={`divide-y ${isDark ? 'divide-white/10' : 'divide-gray-200'}`}>
                              {blogs.map((blog) => (
                                <tr key={blog._id} className={`${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'} transition-colors`}>
                                  <td className="px-6 py-4 max-w-xs">
                                    <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'} line-clamp-2`}>{blog.title}</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="px-2 py-1 text-xs rounded-full bg-green-500/20 text-green-600 dark:text-green-400">
                                      {blog.category}
                                    </span>
                                  </td>
                                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                    {blog.author}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {blog.isLive ? (
                                      <span className="px-2 py-1 text-xs rounded-full bg-green-500/20 text-green-600 dark:text-green-400 flex items-center gap-1 w-fit">
                                        <CheckCircle className="w-3 h-3" />
                                        Live
                                      </span>
                                    ) : (
                                      <span className="px-2 py-1 text-xs rounded-full bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 flex items-center gap-1 w-fit">
                                        <EyeOff className="w-3 h-3" />
                                        Draft
                                      </span>
                                    )}
                                  </td>
                                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                    {blog.views || 0}
                                  </td>
                                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                    {new Date(blog.createdAt).toLocaleDateString()}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <div className="flex items-center justify-end gap-2">
                                      <button
                                        onClick={() => handleToggleLive(blog._id, blog.isLive)}
                                        className="text-blue-500 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300 transition-colors"
                                        title={blog.isLive ? "Unpublish" : "Publish"}
                                      >
                                        {blog.isLive ? <EyeOff className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                                      </button>
                                      <button
                                        onClick={() => openBlogEditModal(blog)}
                                        className="text-yellow-600 dark:text-yellow-400 hover:text-yellow-700 dark:hover:text-yellow-300 transition-colors"
                                        title="Edit"
                                      >
                                        <Edit className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => handleDeleteBlog(blog._id)}
                                        className="text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors"
                                        title="Delete"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        
                        {/* Blog Pagination */}
                        {blogs.length > 0 && (
                          <div className={`px-6 py-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'} flex justify-between items-center`}>
                            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              Showing {((blogCurrentPage - 1) * blogPageSize) + 1} to {Math.min(blogCurrentPage * blogPageSize, blogTotal)} of {blogTotal} blogs
                            </div>
                            <Pagination
                              current={blogCurrentPage}
                              pageSize={blogPageSize}
                              total={blogTotal}
                              onChange={handleBlogPageChange}
                              showSizeChanger
                              showQuickJumper
                              pageSizeOptions={['5', '10', '20', '50']}
                              className="custom-table"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              ) : activeTab === 'newsletter' ? (
                <>
                  {/* Newsletter Subscribers */}
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Newsletter Subscribers</h2>
                        <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                          All subscribed emails from the blog newsletter ({newsletterTotal} total)
                        </p>
                      </div>
                      <button
                        onClick={handleExportSubscribers}
                        className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-600 !text-white rounded-lg text-sm font-medium transition-colors"
                      >
                        <Download className="h-4 w-4" />
                        Export to Excel
                      </button>
                    </div>

                    {/* Search */}
                    <div className="mb-4">
                      <div className="relative max-w-md">
                        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                        <input
                          type="text"
                          placeholder="Search by email..."
                          value={newsletterSearch}
                          onChange={(e) => {
                            setNewsletterSearch(e.target.value);
                            setNewsletterPage(1);
                          }}
                          className={`w-full pl-10 pr-4 py-2 rounded-lg border ${isDark
                            ? 'bg-white/5 border-white/10 text-white placeholder-gray-500'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                          } focus:outline-none focus:ring-2 focus:ring-teal-500/50`}
                        />
                      </div>
                    </div>

                    {/* Table */}
                    {newsletterLoading ? (
                      <Skeleton active paragraph={{ rows: 8 }} />
                    ) : newsletterSubscribers.length === 0 ? (
                      <div className={`text-center py-12 rounded-lg border ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                        <Mail className={`mx-auto h-12 w-12 mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                        <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>No subscribers found</p>
                      </div>
                    ) : (
                      <div>
                        <div className={`rounded-lg border overflow-hidden ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                          <table className="w-full">
                            <thead>
                              <tr className={isDark ? 'bg-white/5' : 'bg-gray-50'}>
                                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>#</th>
                                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Email</th>
                                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Subscribed Date</th>
                              </tr>
                            </thead>
                            <tbody className={`divide-y ${isDark ? 'divide-white/5' : 'divide-gray-100'}`}>
                              {newsletterSubscribers.map((sub, index) => (
                                <tr key={sub._id} className={isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}>
                                  <td className={`px-6 py-4 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {(newsletterPage - 1) * newsletterPageSize + index + 1}
                                  </td>
                                  <td className={`px-6 py-4 text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    {sub.email}
                                  </td>
                                  <td className={`px-6 py-4 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {new Date(sub.subscribedAt).toLocaleDateString('en-US', {
                                      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                                    })}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Pagination */}
                        {newsletterTotal > 0 && (
                          <div className="mt-4 flex justify-end">
                            <Pagination
                              current={newsletterPage}
                              total={newsletterTotal}
                              pageSize={newsletterPageSize}
                              onChange={(page, size) => {
                                setNewsletterPage(page);
                                if (size && size !== newsletterPageSize) setNewsletterPageSize(size);
                              }}
                              showSizeChanger
                              pageSizeOptions={['10', '20', '50', '100']}
                              className="custom-table"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              ) : activeTab === 'leads' ? (
                <>
                  {/* Leads / Demo Requests */}
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Leads</h2>
                        <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                          Demo requests from the landing page ({leadsTotal} total)
                        </p>
                      </div>
                    </div>

                    {/* Filters */}
                    <div className="mb-4 flex flex-wrap gap-3 items-center">
                      <div className="relative flex-1 min-w-[240px] max-w-md">
                        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                        <input
                          type="text"
                          placeholder="Search name / email / company..."
                          value={leadsSearch}
                          onChange={(e) => { setLeadsSearch(e.target.value); setLeadsPage(1); }}
                          className={`w-full pl-10 pr-4 py-2 rounded-lg border ${isDark
                            ? 'bg-white/5 border-white/10 text-white placeholder-gray-500'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                          } focus:outline-none focus:ring-2 focus:ring-teal-500/50`}
                        />
                      </div>
                      <select
                        value={leadsStatusFilter}
                        onChange={(e) => { setLeadsStatusFilter(e.target.value); setLeadsPage(1); }}
                        className={`px-3 py-2 rounded-lg border ${isDark
                          ? 'bg-white/5 border-white/10 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                        } focus:outline-none focus:ring-2 focus:ring-teal-500/50`}
                      >
                        <option value="">All statuses</option>
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="qualified">Qualified</option>
                        <option value="blocked">Blocked</option>
                        <option value="spam">Spam</option>
                      </select>
                    </div>

                    {/* Leads Table */}
                    {leadsLoading ? (
                      <Skeleton active paragraph={{ rows: 8 }} />
                    ) : leads.length === 0 ? (
                      <div className={`text-center py-12 rounded-lg border ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                        <UserCheck className={`mx-auto h-12 w-12 mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                        <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>No leads found</p>
                      </div>
                    ) : (
                      <div>
                        <div className={`rounded-lg border overflow-x-auto ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                          <table className="w-full">
                            <thead>
                              <tr className={isDark ? 'bg-white/5' : 'bg-gray-50'}>
                                <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Date</th>
                                <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Name</th>
                                <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Email</th>
                                <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Company</th>
                                <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Phone</th>
                                <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>IP</th>
                                <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Status</th>
                                <th className={`px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Actions</th>
                              </tr>
                            </thead>
                            <tbody className={`divide-y ${isDark ? 'divide-white/5' : 'divide-gray-100'}`}>
                              {leads.map((lead) => {
                                const statusColors = {
                                  new: 'bg-blue-500/20 text-blue-400',
                                  contacted: 'bg-yellow-500/20 text-yellow-400',
                                  qualified: 'bg-green-500/20 text-green-400',
                                  blocked: 'bg-red-500/20 text-red-400',
                                  spam: 'bg-gray-500/20 text-gray-400',
                                };
                                const isBusy = leadActionLoadingId === lead._id;
                                return (
                                  <tr key={lead._id} className={isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}>
                                    <td className={`px-4 py-3 text-xs whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                      {new Date(lead.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </td>
                                    <td className={`px-4 py-3 text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{lead.fullName}</td>
                                    <td className={`px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{lead.email}</td>
                                    <td className={`px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{lead.companyName || '—'}</td>
                                    <td className={`px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{lead.phone || '—'}</td>
                                    <td className={`px-4 py-3 text-xs font-mono ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{lead.ipAddress || '—'}</td>
                                    <td className="px-4 py-3">
                                      <select
                                        value={lead.status}
                                        disabled={isBusy}
                                        onChange={(e) => handleLeadStatusChange(lead._id, e.target.value)}
                                        className={`text-xs px-2 py-1 rounded font-medium ${statusColors[lead.status] || statusColors.new} border-0 focus:outline-none focus:ring-1 focus:ring-teal-500/50`}
                                        style={{ backgroundColor: 'transparent' }}
                                      >
                                        <option value="new">New</option>
                                        <option value="contacted">Contacted</option>
                                        <option value="qualified">Qualified</option>
                                        <option value="blocked">Blocked</option>
                                        <option value="spam">Spam</option>
                                      </select>
                                    </td>
                                    <td className="px-4 py-3 text-right whitespace-nowrap">
                                      {(() => {
                                        const ipIsBlocked = lead.ipAddress && blockedIps.some(b => b.ipAddress === lead.ipAddress);
                                        if (ipIsBlocked) {
                                          return (
                                            <button
                                              onClick={() => handleUnblockIp(lead.ipAddress)}
                                              disabled={isBusy}
                                              title="Unblock this IP"
                                              className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-teal-500/10 hover:bg-teal-500/20 text-teal-400 disabled:opacity-40 disabled:cursor-not-allowed mr-2"
                                            >
                                              <ShieldOff className="h-3 w-3" />
                                              Unblock
                                            </button>
                                          );
                                        }
                                        return (
                                          <button
                                            onClick={() => handleBlockLeadIp(lead)}
                                            disabled={isBusy || !lead.ipAddress || lead.ipAddress === 'unknown'}
                                            title="Block this IP"
                                            className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-red-500/10 hover:bg-red-500/20 text-red-400 disabled:opacity-40 disabled:cursor-not-allowed mr-2"
                                          >
                                            <Ban className="h-3 w-3" />
                                            Block IP
                                          </button>
                                        );
                                      })()}
                                      <button
                                        onClick={() => handleDeleteLead(lead)}
                                        disabled={isBusy}
                                        title="Delete lead"
                                        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-gray-500/10 hover:bg-gray-500/20 text-gray-400 disabled:opacity-40"
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {leadsTotal > 0 && (
                          <div className="mt-4 flex justify-end">
                            <Pagination
                              current={leadsPage}
                              total={leadsTotal}
                              pageSize={leadsPageSize}
                              onChange={(page, size) => {
                                setLeadsPage(page);
                                if (size && size !== leadsPageSize) setLeadsPageSize(size);
                              }}
                              showSizeChanger
                              pageSizeOptions={['10', '25', '50', '100']}
                              className="custom-table"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Blocked IPs */}
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Blocked IPs</h3>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          IPs that are denied access to the contact form ({blockedIps.length})
                        </p>
                      </div>
                    </div>
                    {blockedIpsLoading ? (
                      <Skeleton active paragraph={{ rows: 3 }} />
                    ) : blockedIps.length === 0 ? (
                      <div className={`text-center py-8 rounded-lg border ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                        <ShieldOff className={`mx-auto h-10 w-10 mb-2 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>No blocked IPs</p>
                      </div>
                    ) : (
                      <div className={`rounded-lg border overflow-hidden ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                        <table className="w-full">
                          <thead>
                            <tr className={isDark ? 'bg-white/5' : 'bg-gray-50'}>
                              <th className={`px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>IP Address</th>
                              <th className={`px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Blocked At</th>
                              <th className={`px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Reason</th>
                              <th className={`px-4 py-2 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Action</th>
                            </tr>
                          </thead>
                          <tbody className={`divide-y ${isDark ? 'divide-white/5' : 'divide-gray-100'}`}>
                            {blockedIps.map((b) => (
                              <tr key={b._id} className={isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}>
                                <td className={`px-4 py-2 text-sm font-mono ${isDark ? 'text-white' : 'text-gray-900'}`}>{b.ipAddress}</td>
                                <td className={`px-4 py-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {new Date(b.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </td>
                                <td className={`px-4 py-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{b.reason || '—'}</td>
                                <td className="px-4 py-2 text-right">
                                  <button
                                    onClick={() => handleUnblockIp(b.ipAddress)}
                                    className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-teal-500/10 hover:bg-teal-500/20 text-teal-400"
                                  >
                                    <ShieldOff className="h-3 w-3" />
                                    Unblock
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              ) : activeTab === 'partners' ? (
                <PartnersManager isDark={isDark} />
              ) : activeTab === 'activity' ? (
                <ActivitySummary isDark={isDark} />
              ) : activeTab === 'posthog' ? (
                <PostHogAnalytics isDark={isDark} />
              ) : activeTab === 'email-campaigns' ? (
                <EmailCampaigns isDark={isDark} />
              ) : activeTab === 'report-generator' ? (
                <ReportGenerator isDark={isDark} />
              ) : null}

              {/* Modals */}
              {/* Add Organization Modal */}
              {showAddModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-md overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
                  <div className="relative w-full max-w-md bg-black/80 backdrop-blur-xl border border-white/20 shadow-2xl rounded-xl">
                    <div className="flex items-center justify-between p-6 border-b border-white/10">
                      <h3 className="text-xl font-semibold text-white">Add Organization</h3>
                      <button
                        onClick={() => setShowAddModal(false)}
                        className="text-gray-400 hover:text-white transition-colors"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <form onSubmit={handleAddOrganization} className="p-6">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-white mb-1">Organization Name</label>
                          <input
                            type="text"
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white/20 focus:border-white/20"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-white mb-1">Email</label>
                          <input
                            type="email"
                            required
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white/20 focus:border-white/20"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-white mb-1">Phone</label>
                          <input
                            type="text"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white/20 focus:border-white/20"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-white mb-1">Website</label>
                          <input
                            type="url"
                            value={formData.website}
                            onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white/20 focus:border-white/20"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-white mb-1">Industry</label>
                          <input
                            type="text"
                            value={formData.industry}
                            onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white/20 focus:border-white/20"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-white mb-1">Plan</label>
                          <select
                            value={formData.plan}
                            onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:ring-2 focus:ring-white/20 focus:border-white/20"
                          >
                            {plans.map((plan) => (
                              <option key={plan.value} value={plan.value}>
                                {plan.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-white mb-1">Status</label>
                          <select
                            value={formData.subscriptionStatus}
                            onChange={(e) => setFormData({ ...formData, subscriptionStatus: e.target.value })}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:ring-2 focus:ring-white/20 focus:border-white/20"
                          >
                            {statuses.map((status) => (
                              <option key={status.value} value={status.value}>
                                {status.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="flex justify-end space-x-3 mt-6">
                        <button
                          type="button"
                          onClick={() => setShowAddModal(false)}
                          className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Add Organization
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* Edit Organization Modal */}
              {showEditModal && selectedOrganization && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-md overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
                  <div className="relative w-full max-w-md bg-black/80 backdrop-blur-xl border border-white/20 shadow-2xl rounded-xl">
                    <div className="flex items-center justify-between p-6 border-b border-white/10">
                      <h3 className="text-xl font-semibold text-white">Edit Organization</h3>
                      <button
                        onClick={() => setShowEditModal(false)}
                        className="text-gray-400 hover:text-white transition-colors"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <form onSubmit={handleEditOrganization} className="p-6">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-white mb-1">Organization Name</label>
                          <input
                            type="text"
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white/20 focus:border-white/20"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-white mb-1">Email</label>
                          <input
                            type="email"
                            required
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white/20 focus:border-white/20"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-white mb-1">Phone</label>
                          <input
                            type="text"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white/20 focus:border-white/20"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-white mb-1">Website</label>
                          <input
                            type="url"
                            value={formData.website}
                            onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white/20 focus:border-white/20"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-white mb-1">Industry</label>
                          <input
                            type="text"
                            value={formData.industry}
                            onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white/20 focus:border-white/20"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-white mb-1">Plan</label>
                          <select
                            value={formData.plan}
                            onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:ring-2 focus:ring-white/20 focus:border-white/20"
                          >
                            {plans.map((plan) => (
                              <option key={plan.value} value={plan.value}>
                                {plan.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-white mb-1">Status</label>
                          <select
                            value={formData.subscriptionStatus}
                            onChange={(e) => setFormData({ ...formData, subscriptionStatus: e.target.value })}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:ring-2 focus:ring-white/20 focus:border-white/20"
                          >
                            {statuses.map((status) => (
                              <option key={status.value} value={status.value}>
                                {status.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="border-t border-white/10 pt-4 mt-4">
                          <h4 className="text-sm font-semibold text-white mb-3">Razorpay Billing Settings</h4>
                          <div>
                            <label className="block text-sm font-medium text-white mb-1">Billing Type</label>
                            <select
                              value={formData.billingType}
                              onChange={(e) => {
                                const newType = e.target.value;
                                setFormData({
                                  ...formData,
                                  billingType: newType,
                                  autoPayEnabled: newType === 'one_time' ? false : formData.autoPayEnabled,
                                });
                                handleBillingTypeChange(selectedOrganization.email, newType);
                              }}
                              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:ring-2 focus:ring-white/20 focus:border-white/20"
                            >
                              <option value="recurring">Recurring (Auto-Renewal)</option>
                              <option value="one_time">One-Time (Manual Renewal)</option>
                            </select>
                            <p className="text-xs text-gray-400 mt-1">
                              {formData.billingType === 'one_time'
                                ? 'User will pay once per cycle and must manually renew after expiry.'
                                : 'Razorpay will auto-charge the user at each billing cycle.'}
                            </p>
                          </div>
                          {formData.billingType === 'recurring' && (
                            <div className="mt-3">
                              <label className="flex items-center justify-between cursor-pointer">
                                <span className="text-sm font-medium text-white">Auto-Pay</span>
                                <div
                                  onClick={() => {
                                    const newVal = !formData.autoPayEnabled;
                                    setFormData({ ...formData, autoPayEnabled: newVal });
                                    handleAutoPayToggle(selectedOrganization.email, newVal);
                                  }}
                                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                                    formData.autoPayEnabled ? 'bg-green-500' : 'bg-gray-600'
                                  }`}
                                >
                                  <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                      formData.autoPayEnabled ? 'translate-x-6' : 'translate-x-1'
                                    }`}
                                  />
                                </div>
                              </label>
                              <p className="text-xs text-gray-400 mt-1">
                                {formData.autoPayEnabled
                                  ? 'Subscription will auto-renew at cycle end.'
                                  : 'Subscription will cancel at cycle end. User must manually re-subscribe.'}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-end space-x-3 mt-6">
                        <button
                          type="button"
                          onClick={() => setShowEditModal(false)}
                          className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-4 py-2 bg-blue-600 !text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Update Organization
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* AI Prompt Modal */}
              {showPromptModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-md overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
                  <div className="relative w-full max-w-2xl bg-black/80 backdrop-blur-xl border border-white/20 shadow-2xl rounded-xl">
                    <div className="flex items-center justify-between p-6 border-b border-white/10">
                      <h3 className="text-xl font-semibold text-white">
                        {selectedPrompt ? 'Edit AI Prompt' : 'Add AI Prompt'}
                      </h3>
                      <button
                        onClick={() => {
                          setShowPromptModal(false);
                          setSelectedPrompt(null);
                          resetPromptForm();
                        }}
                        className="text-gray-400 hover:text-white transition-colors"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <form onSubmit={selectedPrompt ? handleEditPrompt : handleAddPrompt} className="p-6">
                      <div className="space-y-4">
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-white mb-1">Prompt Type</label>
                            <select
                              value={promptFormData.promptType}
                              onChange={(e) => setPromptFormData({ ...promptFormData, promptType: e.target.value })}
                              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:ring-2 focus:ring-white/20 focus:border-white/20"
                            >
                              <option value="hourly">Hourly</option>
                              <option value="daily">Daily</option>
                              <option value="position">Position</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-white mb-1">GPT Model</label>
                            <select
                              value={promptFormData.gptModel}
                              onChange={(e) => handleModelChange(e.target.value)}
                              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:ring-2 focus:ring-white/20 focus:border-white/20"
                            >
                              <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                              <option value="gpt-4">GPT-4</option>
                              <option value="gpt-4-turbo">GPT-4 Turbo</option>
                              <option value="gpt-4o">GPT-4o</option>
                              <option value="gpt-4o-mini">GPT-4o Mini</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-white mb-1">Status</label>
                            <select
                              value={promptFormData.isActive}
                              onChange={(e) => setPromptFormData({ ...promptFormData, isActive: e.target.value === 'true' })}
                              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:ring-2 focus:ring-white/20 focus:border-white/20"
                            >
                              <option value="true">Active</option>
                              <option value="false">Inactive</option>
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-white mb-1">Title</label>
                          <input
                            type="text"
                            required
                            value={promptFormData.title}
                            onChange={(e) => setPromptFormData({ ...promptFormData, title: e.target.value })}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white/20 focus:border-white/20"
                            placeholder="Enter prompt title..."
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-white mb-1">Description</label>
                          <input
                            type="text"
                            value={promptFormData.description}
                            onChange={(e) => setPromptFormData({ ...promptFormData, description: e.target.value })}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white/20 focus:border-white/20"
                            placeholder="Optional description..."
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-white mb-1">Prompt Content</label>
                          <textarea
                            required
                            rows={8}
                            value={promptFormData.prompt}
                            onChange={(e) => setPromptFormData({ ...promptFormData, prompt: e.target.value })}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white/20 focus:border-white/20"
                            placeholder="Enter the AI prompt content..."
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-white mb-1">
                              Max Tokens
                              <span className="text-xs text-gray-400 ml-1">
                                (Model limit: {getMaxTokensForModel(promptFormData.gptModel)})
                              </span>
                            </label>
                            <input
                              type="number"
                              min="100"
                              max={getMaxTokensForModel(promptFormData.gptModel)}
                              value={promptFormData.maxTokens}
                              onChange={(e) => setPromptFormData({ ...promptFormData, maxTokens: parseInt(e.target.value) })}
                              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:ring-2 focus:ring-white/20 focus:border-white/20"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-white mb-1">Temperature</label>
                            <input
                              type="number"
                              min="0"
                              max="2"
                              step="0.1"
                              value={promptFormData.temperature}
                              onChange={(e) => setPromptFormData({ ...promptFormData, temperature: parseFloat(e.target.value) })}
                              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:ring-2 focus:ring-white/20 focus:border-white/20"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end space-x-3 mt-6">
                        <button
                          type="button"
                          onClick={() => {
                            setShowPromptModal(false);
                            setSelectedPrompt(null);
                            resetPromptForm();
                          }}
                          className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-4 py-2 bg-blue-600 !text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          {selectedPrompt ? 'Update Prompt' : 'Add Prompt'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* Blog Modal */}
              {showBlogModal && (
                <div className={`fixed inset-0 ${isDark ? 'bg-black/60' : 'bg-black/40'} backdrop-blur-md overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4`}>
                  <div className={`relative w-full max-w-4xl ${isDark ? 'bg-black/80 border-white/20' : 'bg-white border-gray-200'} backdrop-blur-xl border shadow-2xl rounded-xl`}>
                    <div className={`flex items-center justify-between p-6 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                      <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {selectedBlog ? 'Edit Blog' : 'Create New Blog'}
                      </h3>
                      <button
                        onClick={() => {
                          setShowBlogModal(false);
                          setSelectedBlog(null);
                          resetBlogForm();
                        }}
                        className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'} transition-colors`}
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <form onSubmit={selectedBlog ? handleEditBlog : handleAddBlog} className="p-6 max-h-[80vh] overflow-y-auto">
                      <div className="space-y-4">
                        <div>
                          <label className={`block text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'} mb-1`}>Title *</label>
                          <input
                            type="text"
                            required
                            value={blogFormData.title}
                            onChange={(e) => setBlogFormData({ ...blogFormData, title: e.target.value })}
                            className={`w-full px-3 py-2 ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-400' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'} border rounded-lg focus:ring-2 focus:ring-[#00D1C1]/50 focus:border-[#00D1C1]`}
                            placeholder="Enter blog title..."
                          />
                        </div>
                        <div>
                          <label className={`block text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'} mb-1`}>Slug <span className={`text-xs font-normal ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>(the URL of this blog)</span></label>
                          <input
                            type="text"
                            value={blogFormData.slug}
                            onChange={(e) => {
                              const s = e.target.value
                                .toLowerCase()
                                .replace(/[^a-z0-9]+/g, '-')
                                .replace(/^-+/, '');
                              setBlogFormData({ ...blogFormData, slug: s });
                            }}
                            className={`w-full px-3 py-2 ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-400' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'} border rounded-lg focus:ring-2 focus:ring-[#00D1C1]/50 focus:border-[#00D1C1]`}
                            placeholder="Leave empty to auto-generate from title"
                          />
                          <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            Page will open at <span className="font-mono">https://teamgrid.ai/blog/{blogFormData.slug || '<auto-from-title>'}</span>
                          </p>
                        </div>
                        <div>
                          <label className={`block text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'} mb-1`}>Description *</label>
                          <textarea
                            required
                            rows={3}
                            value={blogFormData.description}
                            onChange={(e) => setBlogFormData({ ...blogFormData, description: e.target.value })}
                            className={`w-full px-3 py-2 ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-400' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'} border rounded-lg focus:ring-2 focus:ring-[#00D1C1]/50 focus:border-[#00D1C1]`}
                            placeholder="Enter blog description (shown on listing page)..."
                          />
                        </div>
                        <div>
                          <label className={`block text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'} mb-1`}>Content *</label>
                          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>Use the toolbar to format your content. Click the image icon to insert images.</p>
                          <TipTapEditor
                              value={blogFormData.content}
                              onChange={(value) => setBlogFormData(prev => ({ ...prev, content: value }))}
                              placeholder="Start writing your blog content here..."
                            isDark={isDark}
                            onImageUpload={handleImageUploadInEditor}
                            uploadingImages={uploadingImages}
                            editorRef={tipTapEditorRef}
                          />
                        </div>
                        <div>
                          <label className={`block text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'} mb-1`}>Category *</label>
                          <select
                            required
                            value={blogFormData.category}
                            onChange={(e) => setBlogFormData({ ...blogFormData, category: e.target.value })}
                            className={`w-full px-3 py-2 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-lg focus:ring-2 focus:ring-[#00D1C1]/50 focus:border-[#00D1C1]`}
                          >
                            <option value="AI & Automation">AI & Automation</option>
                            <option value="Productivity">Productivity</option>
                            <option value="Remote Work">Remote Work</option>
                            <option value="Team Management">Team Management</option>
                          </select>
                        </div>
                        <div>
                          <label className={`block text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'} mb-1`}>Product *</label>
                          <select
                            required
                            value={blogFormData.product}
                            onChange={(e) => setBlogFormData({ ...blogFormData, product: e.target.value })}
                            className={`w-full px-3 py-2 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-lg focus:ring-2 focus:ring-[#00D1C1]/50 focus:border-[#00D1C1]`}
                          >
                            <option value="Teamgrid">Teamgrid</option>
                            <option value="Seller7">Seller7</option>
                          </select>
                        </div>
                        {/* Read Time - Commented out */}
                        {/* <div>
                          <label className={`block text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'} mb-1`}>Read Time</label>
                          <input
                            type="text"
                            value={blogFormData.readTime}
                            onChange={(e) => setBlogFormData({ ...blogFormData, readTime: e.target.value })}
                            className={`w-full px-3 py-2 ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-400' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'} border rounded-lg focus:ring-2 focus:ring-[#00D1C1]/50 focus:border-[#00D1C1]`}
                            placeholder="e.g., 5 min"
                          />
                        </div> */}
                        <div>
                          <label className={`block text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'} mb-1`}>Author Name *</label>
                          <input
                            type="text"
                            required
                            value={blogFormData.author}
                            onChange={(e) => setBlogFormData({ ...blogFormData, author: e.target.value })}
                            className={`w-full px-3 py-2 ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-400' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'} border rounded-lg focus:ring-2 focus:ring-[#00D1C1]/50 focus:border-[#00D1C1]`}
                            placeholder="e.g., Jessica Davis"
                          />
                        </div>
                        {/* Author Initials - Commented out */}
                        {/* <div>
                          <label className={`block text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'} mb-1`}>Author Initials</label>
                          <input
                            type="text"
                            maxLength={2}
                            value={blogFormData.authorInitials}
                            onChange={(e) => setBlogFormData({ ...blogFormData, authorInitials: e.target.value.toUpperCase() })}
                            className={`w-full px-3 py-2 ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-400' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'} border rounded-lg focus:ring-2 focus:ring-[#00D1C1]/50 focus:border-[#00D1C1]`}
                            placeholder="e.g., JD"
                          />
                        </div> */}
                        <div>
                          <label className={`block text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'} mb-2`}>Featured Image (Optional)</label>
                          
                          {/* URL Input */}
                          <div className="mb-3">
                            <label className={`block text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Image URL</label>
                            <input
                              type="url"
                              value={blogFormData.featuredImage}
                              onChange={(e) => {
                                setBlogFormData({ ...blogFormData, featuredImage: e.target.value });
                                setFeaturedImagePreview(e.target.value);
                              }}
                              className={`w-full px-3 py-2 ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-400' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'} border rounded-lg focus:ring-2 focus:ring-[#00D1C1]/50 focus:border-[#00D1C1]`}
                              placeholder="https://example.com/image.webp"
                            />
                          </div>

                          {/* OR Divider */}
                          <div className="flex items-center my-3">
                            <div className={`flex-1 h-px ${isDark ? 'bg-white/10' : 'bg-gray-300'}`}></div>
                            <span className={`px-3 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>OR</span>
                            <div className={`flex-1 h-px ${isDark ? 'bg-white/10' : 'bg-gray-300'}`}></div>
                          </div>

                          {/* Upload Option */}
                          <div className="mb-3">
                            <label className={`block text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Upload Image</label>
                            <div className={`border-2 border-dashed ${isDark ? 'border-white/20' : 'border-gray-300'} rounded-lg p-4`}>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleFeaturedImageUpload(e.target.files[0])}
                                disabled={uploadingFeaturedImage}
                                className="hidden"
                                id="featured-image-upload"
                              />
                              <label
                                htmlFor="featured-image-upload"
                                className={`cursor-pointer flex flex-col items-center justify-center py-2 ${uploadingFeaturedImage ? 'opacity-50 cursor-not-allowed' : ''}`}
                              >
                                <svg className={`w-8 h-8 mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                  {uploadingFeaturedImage ? 'Uploading...' : 'Click to upload featured image'}
                                </span>
                                <span className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                                  PNG, JPG, GIF up to 10MB
                                </span>
                              </label>
                            </div>
                          </div>

                          {/* Image Preview */}
                          {(featuredImagePreview || blogFormData.featuredImage) && (
                            <div className="mt-3">
                              <label className={`block text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>Preview</label>
                              <div className="relative inline-block">
                                <img
                                  src={featuredImagePreview || blogFormData.featuredImage}
                                  alt="Featured image preview"
                                  className={`w-32 h-32 object-cover rounded-lg border ${isDark ? 'border-white/20' : 'border-gray-300'} ${uploadingFeaturedImage ? 'opacity-50' : ''}`}
                                />
                                {uploadingFeaturedImage && (
                                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-lg">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#00D1C1]"></div>
                                  </div>
                                )}
                                <button
                                  type="button"
                                  onClick={removeFeaturedImage}
                                  disabled={uploadingFeaturedImage}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  ×
                                </button>
                              </div>
                              {uploadingFeaturedImage && (
                                <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                  Uploading to server...
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                        {/* <div>
                          <label className={`block text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'} mb-2`}>Blog Images</label>
                          <div className={`border-2 border-dashed ${isDark ? 'border-white/20' : 'border-gray-300'} rounded-lg p-4`}>
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={(e) => handleImageUpload(e.target.files)}
                              disabled={uploadingImages}
                              className="hidden"
                              id="blog-image-upload"
                            />
                            <label
                              htmlFor="blog-image-upload"
                              className={`cursor-pointer flex flex-col items-center justify-center py-4 ${uploadingImages ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <svg className={`w-10 h-10 mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                {uploadingImages ? 'Uploading...' : 'Click to upload images or drag and drop'}
                              </span>
                              <span className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                                PNG, JPG, GIF up to 10MB (multiple files allowed)
                              </span>
                            </label>
                          </div>
                          {uploadedImages.length > 0 && (
                            <div className="mt-4 grid grid-cols-3 gap-4">
                              {uploadedImages.map((image, index) => (
                                <div key={image.key} className="relative group">
                                  <img
                                    src={image.url}
                                    alt={`Upload ${index + 1}`}
                                    className="w-full h-32 object-cover rounded-lg border border-gray-300"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveImage(image.key)}
                                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div> */}
                        {/* Make Live - Commented out */}
                        {/* <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            id="isLive"
                            checked={blogFormData.isLive}
                            onChange={(e) => setBlogFormData({ ...blogFormData, isLive: e.target.checked })}
                            className={`w-4 h-4 text-[#00D1C1] ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-300'} rounded focus:ring-[#00D1C1]`}
                          />
                          <label htmlFor="isLive" className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>
                            Make Live (visible to all organizations)
                          </label>
                        </div> */}

                        {/* SEO Settings Section */}
                        <div>
                          <button
                            type="button"
                            onClick={() => setShowSeoSettings(!showSeoSettings)}
                            className={`flex items-center gap-2 text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'} mb-2`}
                          >
                            <svg className={`w-4 h-4 transition-transform ${showSeoSettings ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                            SEO Settings <span className={`text-xs font-normal ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>(Optional)</span>
                          </button>
                          {showSeoSettings && (
                            <div className={`p-4 rounded-lg border ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'} space-y-3`}>
                              <div>
                                <div className="flex items-center justify-between mb-1">
                                  <label className={`block text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Meta Title</label>
                                  <span className={`text-xs ${(blogFormData.metaTitle?.length || 0) > 60 ? 'text-amber-500' : isDark ? 'text-gray-500' : 'text-gray-400'}`}>{blogFormData.metaTitle?.length || 0}/60</span>
                                </div>
                                <input
                                  type="text"
                                  value={blogFormData.metaTitle}
                                  onChange={(e) => setBlogFormData({ ...blogFormData, metaTitle: e.target.value })}
                                  className={`w-full px-3 py-2 text-sm ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} border rounded-lg focus:ring-2 focus:ring-[#00D1C1]/50 focus:border-[#00D1C1]`}
                                  placeholder="Custom meta title (defaults to blog title)"
                                  maxLength={120}
                                />
                              </div>
                              <div>
                                <div className="flex items-center justify-between mb-1">
                                  <label className={`block text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Meta Description</label>
                                  <span className={`text-xs ${(blogFormData.metaDescription?.length || 0) > 160 ? 'text-amber-500' : isDark ? 'text-gray-500' : 'text-gray-400'}`}>{blogFormData.metaDescription?.length || 0}/160</span>
                                </div>
                                <textarea
                                  rows={2}
                                  value={blogFormData.metaDescription}
                                  onChange={(e) => setBlogFormData({ ...blogFormData, metaDescription: e.target.value })}
                                  className={`w-full px-3 py-2 text-sm ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} border rounded-lg focus:ring-2 focus:ring-[#00D1C1]/50 focus:border-[#00D1C1]`}
                                  placeholder="Custom meta description (defaults to blog description)"
                                  maxLength={320}
                                />
                              </div>
                              <div>
                                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Keywords <span className={`font-normal ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>(comma-separated)</span></label>
                                <input
                                  type="text"
                                  value={(blogFormData.keywords || []).join(', ')}
                                  onChange={(e) => {
                                    const kw = e.target.value.split(',').map(k => k.trim()).filter(Boolean);
                                    setBlogFormData({ ...blogFormData, keywords: kw });
                                  }}
                                  className={`w-full px-3 py-2 text-sm ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} border rounded-lg focus:ring-2 focus:ring-[#00D1C1]/50 focus:border-[#00D1C1]`}
                                  placeholder="e.g. workforce analytics, team productivity, AI insights"
                                />
                              </div>
                              <div>
                                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Canonical URL <span className={`font-normal ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>(SEO only — not the page URL)</span></label>
                                <input
                                  type="text"
                                  value={blogFormData.canonicalUrl}
                                  onChange={(e) => setBlogFormData({ ...blogFormData, canonicalUrl: e.target.value })}
                                  className={`w-full px-3 py-2 text-sm ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} border rounded-lg focus:ring-2 focus:ring-[#00D1C1]/50 focus:border-[#00D1C1]`}
                                  placeholder="just-the-slug  (or paste a full https:// URL)"
                                  maxLength={500}
                                />
                                <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                  Enter just a slug (e.g. <span className="font-mono">my-article</span>) and it becomes <span className="font-mono">https://teamgrid.ai/blog/my-article</span>. Paste a full <span className="font-mono">https://</span> URL to point anywhere (e.g. another site).
                                </p>
                              </div>
                              <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                Leave fields empty to auto-generate from blog title and description. Canonical URL defaults to this blog's own URL when blank.
                              </p>
                            </div>
                          )}
                        </div>

                        {/* FAQ Section */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className={`block text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>
                              FAQs <span className={`text-xs font-normal ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>(Optional · max 5)</span>
                            </label>
                            {blogFormData.faqs.length < 5 && (
                              <button
                                type="button"
                                onClick={() => setBlogFormData({ ...blogFormData, faqs: [...blogFormData.faqs, { question: '', answer: '' }] })}
                                className="flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-lg bg-[#00D1C1]/10 text-[#00D1C1] hover:bg-[#00D1C1]/20 transition-colors"
                              >
                                + Add FAQ
                              </button>
                            )}
                          </div>
                          {blogFormData.faqs.length === 0 && (
                            <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No FAQs added yet. Click "Add FAQ" to add up to 5 FAQs for this blog.</p>
                          )}
                          <div className="space-y-3">
                            {blogFormData.faqs.map((faq, index) => (
                              <div key={index} className={`p-3 rounded-lg border ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                                <div className="flex items-center justify-between mb-2">
                                  <span className={`text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>FAQ {index + 1}</span>
                                  <button
                                    type="button"
                                    onClick={() => setBlogFormData({ ...blogFormData, faqs: blogFormData.faqs.filter((_, i) => i !== index) })}
                                    className="text-xs text-red-500 hover:text-red-600 transition-colors"
                                  >
                                    Remove
                                  </button>
                                </div>
                                <input
                                  type="text"
                                  value={faq.question}
                                  onChange={(e) => {
                                    const updated = [...blogFormData.faqs];
                                    updated[index] = { ...updated[index], question: e.target.value };
                                    setBlogFormData({ ...blogFormData, faqs: updated });
                                  }}
                                  className={`w-full px-3 py-2 mb-2 text-sm ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} border rounded-lg focus:ring-2 focus:ring-[#00D1C1]/50 focus:border-[#00D1C1]`}
                                  placeholder="Question"
                                />
                                <textarea
                                  rows={2}
                                  value={faq.answer}
                                  onChange={(e) => {
                                    const updated = [...blogFormData.faqs];
                                    updated[index] = { ...updated[index], answer: e.target.value };
                                    setBlogFormData({ ...blogFormData, faqs: updated });
                                  }}
                                  className={`w-full px-3 py-2 text-sm ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} border rounded-lg focus:ring-2 focus:ring-[#00D1C1]/50 focus:border-[#00D1C1]`}
                                  placeholder="Answer"
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end space-x-3 mt-6">
                        <button
                          type="button"
                          onClick={() => {
                            setShowBlogModal(false);
                            setSelectedBlog(null);
                            resetBlogForm();
                          }}
                          className={`px-4 py-2 ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-800'} transition-colors`}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-4 py-2 bg-[#00D1C1] !text-white font-semibold rounded-lg hover:bg-[#00D1C1]/90 transition-colors"
                        >
                          {selectedBlog ? 'Update Blog' : 'Create Blog'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
      </div>
    </div>
    </AdminThemeProvider>
    </>
  );
};

export default MainAdmin;
